import React, { useState, useCallback } from "react";
import { Box, Typography, useTheme, Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  AttachMoney,
  TrendingUp,
  AccountBalance,
  InfoOutlined,
} from "@mui/icons-material";
import DataTableReport from "components/reports/templates/DataTableReport";
import DashboardReport from "components/reports/templates/DashboardReport";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import {
  formatPercent as formatPercentValue,
  isComparable,
} from "components/reports/shared/reportFormatters";
import { HtmlTooltip } from "components/HeatMap";
import { getChartColors } from "theme";
import api from "services/api";

const sharePercent = (part, total, digits = 2) => {
  const numerator = Number(part);
  const denominator = Number(total);
  if (
    !Number.isFinite(numerator) ||
    !Number.isFinite(denominator) ||
    denominator <= 0
  ) {
    return `${(0).toFixed(digits)}%`;
  }
  return `${((numerator / denominator) * 100).toFixed(digits)}%`;
};

const formatNullableCurrency = (value) => {
  if (value === null || value === undefined) return "n/a";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "n/a";
  return `$${numeric.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Revenue Summary Report - Dashboard view with key revenue metrics
 */
export const RevenueSummary = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = () => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  };

  const dataFetchFn = useCallback(async (filters) => {
    const response = await api.get("/reports/financial/revenue-summary", {
      params: filters,
    });
    return response.data;
  }, []);

  // P&L Drill-down handlers
  const handleTotalRevenueClick = useCallback((data) => {
    // Construct monthly breakdown from monthlyTrend data
    const monthlyBreakdown = (data.monthlyTrend?.months || []).map(
      (month, index) => ({
        month,
        revenue: data.monthlyTrend?.revenue?.[index] || 0,
        interpretationRevenue:
          data.monthlyTrend?.interpretationRevenue?.[index] || 0,
        translationRevenue: data.monthlyTrend?.translationRevenue?.[index] || 0,
        otherServicesRevenue:
          data.monthlyTrend?.otherServicesRevenue?.[index] || 0,
        servicesCount: data.monthlyTrend?.servicesCount?.[index] || 0,
        interpretationServicesCount:
          data.monthlyTrend?.interpretationServicesCount?.[index] || 0,
        translationServicesCount:
          data.monthlyTrend?.translationServicesCount?.[index] || 0,
        otherServicesCount: data.monthlyTrend?.otherServicesCount?.[index] || 0,
        avgPerService:
          (data.monthlyTrend?.revenue?.[index] || 0) /
          (data.monthlyTrend?.servicesCount?.[index] || 1),
        yoyGrowth: data.monthlyTrend?.yoyGrowth?.[index] || 0,
      }),
    );

    // Find best and worst months
    const bestMonth = monthlyBreakdown.reduce(
      (best, current) =>
        current.revenue > (best?.revenue || 0) ? current : best,
      monthlyBreakdown[0],
    );
    const worstMonth = monthlyBreakdown.reduce(
      (worst, current) =>
        current.revenue < (worst?.revenue || Infinity) ? current : worst,
      monthlyBreakdown[0],
    );

    setDrillDown({
      open: true,
      title: "Total Revenue - Monthly Breakdown",
      subtitle: `${monthlyBreakdown.length} months • $${data.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total`,
      description: (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            Revenue Breakdown by Month & Service Type
          </Typography>
          <Typography variant="body2" paragraph>
            This table shows your total revenue broken down by month and service
            type (Translation, Interpretation, Other Services). Use this to
            identify seasonal patterns, service mix trends, and high/low
            performance months.
          </Typography>
        </Box>
      ),
      data: monthlyBreakdown,
      columns: [
        { field: "month", headerName: "Month", width: 130 },
        {
          field: "revenue",
          headerName: "Total Revenue",
          width: 140,
          renderCell: (row) =>
            `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "interpretationRevenue",
          headerName: "Interpretation",
          width: 140,
          renderCell: (row) =>
            `$${row.interpretationRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "translationRevenue",
          headerName: "Translation",
          width: 130,
          renderCell: (row) =>
            `$${row.translationRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "otherServicesRevenue",
          headerName: "Other Services",
          width: 140,
          renderCell: (row) =>
            `$${row.otherServicesRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "servicesCount",
          headerName: "Services",
          width: 100,
        },
        {
          field: "avgPerService",
          headerName: "Avg/Service",
          width: 120,
          renderCell: (row) =>
            `$${row.avgPerService?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "yoyGrowth",
          headerName: "YoY Growth",
          width: 110,
          renderCell: (row) =>
            formatPercentValue(row.yoyGrowth, { digits: 1, signed: true }),
        },
      ],
      summary: {
        metrics: [
          {
            label: "Total Revenue",
            value: `$${data.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "success.main",
          },
          {
            label: "Avg Monthly Revenue",
            value: `$${(data.totalRevenue / (monthlyBreakdown.length || 1))?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
          {
            label: "Total Services",
            value:
              monthlyBreakdown
                .reduce((sum, m) => sum + (m.servicesCount || 0), 0)
                ?.toLocaleString() || "0",
          },
          {
            label: "YoY Growth",
            value: formatPercentValue(data.yoyGrowth, { digits: 1, signed: true }),
            color: isComparable(data.yoyGrowth) ? (data.yoyGrowth > 0 ? "success.main" : "error.main") : "text.secondary",
          },
          {
            label: "Best Month",
            value: `${bestMonth?.month} • $${bestMonth?.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "success.main",
          },
          {
            label: "Worst Month",
            value: `${worstMonth?.month} • $${worstMonth?.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "error.main",
          },
        ],
      },
    });
  }, []);

  const handleCostOfRevenueClick = useCallback((data) => {
    // Transform monthlyTrend object into breakdown array
    const monthlyBreakdown = (data.monthlyTrend?.months || []).map(
      (month, index) => ({
        month,
        costs: data.monthlyTrend?.costs?.[index] || 0,
        interpretationCosts:
          data.monthlyTrend?.interpretationCosts?.[index] || 0,
        translationCosts: data.monthlyTrend?.translationCosts?.[index] || 0,
        otherServicesCosts: data.monthlyTrend?.otherServicesCosts?.[index] || 0,
        servicesCount: data.monthlyTrend?.servicesCount?.[index] || 0,
        interpretationServicesCount:
          data.monthlyTrend?.interpretationServicesCount?.[index] || 0,
        translationServicesCount:
          data.monthlyTrend?.translationServicesCount?.[index] || 0,
        otherServicesCount: data.monthlyTrend?.otherServicesCount?.[index] || 0,
        avgPerService:
          (data.monthlyTrend?.costs?.[index] || 0) /
          (data.monthlyTrend?.servicesCount?.[index] || 1),
        yoyGrowth: data.monthlyTrend?.yoyGrowth?.[index] || 0,
      }),
    );

    // Find best and worst months (best = lowest cost, worst = highest cost)
    const bestMonth = monthlyBreakdown.reduce(
      (best, current) =>
        current.costs < (best?.costs || Infinity) ? current : best,
      monthlyBreakdown[0],
    );
    const worstMonth = monthlyBreakdown.reduce(
      (worst, current) =>
        current.costs > (worst?.costs || 0) ? current : worst,
      monthlyBreakdown[0],
    );

    setDrillDown({
      open: true,
      title: "Cost of Revenue - Monthly Breakdown",
      subtitle: `${monthlyBreakdown.length} months • $${data.costOfRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total`,
      description: (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            Direct Service Costs by Month
          </Typography>
          <Typography variant="body2" paragraph>
            Monthly breakdown of direct costs for delivering services -
            primarily linguist payments and direct service delivery costs. Track
            how costs fluctuate with revenue to maintain healthy profit margins.
          </Typography>
        </Box>
      ),
      data: monthlyBreakdown,
      columns: [
        { field: "month", headerName: "Month", width: 130 },
        {
          field: "costs",
          headerName: "Total Costs",
          width: 140,
          renderCell: (row) =>
            `$${row.costs?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "interpretationCosts",
          headerName: "Interpretation",
          width: 140,
          renderCell: (row) =>
            `$${row.interpretationCosts?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "translationCosts",
          headerName: "Translation",
          width: 130,
          renderCell: (row) =>
            `$${row.translationCosts?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "otherServicesCosts",
          headerName: "Other Services",
          width: 140,
          renderCell: (row) =>
            `$${row.otherServicesCosts?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "servicesCount",
          headerName: "Services",
          width: 100,
        },
        {
          field: "avgPerService",
          headerName: "Avg/Service",
          width: 120,
          renderCell: (row) =>
            `$${row.avgPerService?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "yoyGrowth",
          headerName: "YoY Growth",
          width: 110,
          renderCell: (row) =>
            formatPercentValue(row.yoyGrowth, { digits: 1, signed: true }),
        },
      ],
      summary: {
        metrics: [
          {
            label: "Total Costs",
            value: `$${data.costOfRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "error.main",
          },
          {
            label: "Avg Monthly Cost",
            value: `$${(data.costOfRevenue / (monthlyBreakdown.length || 1))?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
          {
            label: "Total Services",
            value:
              monthlyBreakdown
                .reduce((sum, m) => sum + (m.servicesCount || 0), 0)
                ?.toLocaleString() || "0",
          },
          {
            label: "YoY Growth",
            value: formatPercentValue(data.yoyGrowth, { digits: 1, signed: true }),
            color: isComparable(data.yoyGrowth) ? (data.yoyGrowth > 0 ? "error.main" : "success.main") : "text.secondary",
          },
          {
            label: "Best Month (Lowest)",
            value: `${bestMonth?.month} • $${bestMonth?.costs?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "success.main",
          },
          {
            label: "Worst Month (Highest)",
            value: `${worstMonth?.month} • $${worstMonth?.costs?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "error.main",
          },
        ],
      },
    });
  }, []);

  const handleGrossProfitClick = useCallback((data) => {
    // Transform monthlyTrend object into breakdown array
    const monthlyBreakdown = (data.monthlyTrend?.months || []).map(
      (month, index) => {
        const revenue = data.monthlyTrend?.revenue?.[index] || 0;
        const costs = data.monthlyTrend?.costs?.[index] || 0;
        const translationRevenue =
          data.monthlyTrend?.translationRevenue?.[index] || 0;
        const translationCosts =
          data.monthlyTrend?.translationCosts?.[index] || 0;
        const interpretationRevenue =
          data.monthlyTrend?.interpretationRevenue?.[index] || 0;
        const interpretationCosts =
          data.monthlyTrend?.interpretationCosts?.[index] || 0;
        const otherServicesRevenue =
          data.monthlyTrend?.otherServicesRevenue?.[index] || 0;
        const otherServicesCosts =
          data.monthlyTrend?.otherServicesCosts?.[index] || 0;

        return {
          month,
          grossProfit: revenue - costs,
          interpretationGrossProfit:
            interpretationRevenue - interpretationCosts,
          translationGrossProfit: translationRevenue - translationCosts,
          otherServicesGrossProfit: otherServicesRevenue - otherServicesCosts,
          revenue,
          costs,
          translationRevenue,
          interpretationRevenue,
          otherServicesRevenue,
          translationCosts,
          interpretationCosts,
          otherServicesCosts,
          servicesCount: data.monthlyTrend?.servicesCount?.[index] || 0,
          interpretationServicesCount:
            data.monthlyTrend?.interpretationServicesCount?.[index] || 0,
          translationServicesCount:
            data.monthlyTrend?.translationServicesCount?.[index] || 0,
          otherServicesCount:
            data.monthlyTrend?.otherServicesCount?.[index] || 0,
          avgPerService:
            (revenue - costs) /
            (data.monthlyTrend?.servicesCount?.[index] || 1),
          yoyGrowth: data.monthlyTrend?.yoyGrowth?.[index] || 0,
        };
      },
    );

    // Find best and worst months (based on gross profit)
    const bestMonth = monthlyBreakdown.reduce(
      (best, current) =>
        current.grossProfit > (best?.grossProfit || 0) ? current : best,
      monthlyBreakdown[0],
    );
    const worstMonth = monthlyBreakdown.reduce(
      (worst, current) =>
        current.grossProfit < (worst?.grossProfit || Infinity)
          ? current
          : worst,
      monthlyBreakdown[0],
    );

    setDrillDown({
      open: true,
      title: "Gross Profit - Monthly Breakdown",
      subtitle: `${monthlyBreakdown.length} months • $${data.grossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total`,
      description: (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            Gross Profit Performance by Month
          </Typography>
          <Typography variant="body2" paragraph>
            Monthly breakdown of Gross Profit (Revenue minus Cost of Revenue).
            This shows how much you're earning after paying for direct service
            delivery, but before operating expenses. Monitor trends to ensure
            margins remain healthy.
          </Typography>
        </Box>
      ),
      data: monthlyBreakdown,
      columns: [
        { field: "month", headerName: "Month", width: 130 },
        {
          field: "grossProfit",
          headerName: "Total Gross Profit",
          width: 150,
          renderCell: (row) =>
            `$${row.grossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "interpretationGrossProfit",
          headerName: "Interpretation",
          width: 140,
          renderCell: (row) =>
            `$${row.interpretationGrossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "translationGrossProfit",
          headerName: "Translation",
          width: 130,
          renderCell: (row) =>
            `$${row.translationGrossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "otherServicesGrossProfit",
          headerName: "Other Services",
          width: 140,
          renderCell: (row) =>
            `$${row.otherServicesGrossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "servicesCount",
          headerName: "Services",
          width: 100,
        },
        {
          field: "avgPerService",
          headerName: "Avg/Service",
          width: 120,
          renderCell: (row) =>
            `$${row.avgPerService?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "yoyGrowth",
          headerName: "YoY Growth",
          width: 110,
          renderCell: (row) =>
            formatPercentValue(row.yoyGrowth, { digits: 1, signed: true }),
        },
      ],
      summary: {
        metrics: [
          {
            label: "Total Gross Profit",
            value: `$${data.grossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "success.main",
          },
          {
            label: "Avg Monthly",
            value: `$${(data.grossProfit / (monthlyBreakdown.length || 1))?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
          {
            label: "Total Services",
            value:
              monthlyBreakdown
                .reduce((sum, m) => sum + (m.servicesCount || 0), 0)
                ?.toLocaleString() || "0",
          },
          {
            label: "YoY Growth",
            value: formatPercentValue(data.yoyGrowth, { digits: 1, signed: true }),
            color: isComparable(data.yoyGrowth) ? (data.yoyGrowth > 0 ? "success.main" : "error.main") : "text.secondary",
          },
          {
            label: "Best Month",
            value: `${bestMonth?.month} • $${bestMonth?.grossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "success.main",
          },
          {
            label: "Worst Month",
            value: `${worstMonth?.month} • $${worstMonth?.grossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "error.main",
          },
        ],
      },
    });
  }, []);

  const handleOperatingExpensesClick = useCallback((data) => {
    // Always use the opex and months arrays from monthlyTrend
    const monthlyBreakdown = (data.monthlyTrend?.months || []).map(
      (month, index) => {
        const opexValue = data.monthlyTrend?.opex?.[index] || 0;
        const categoriesData =
          data.monthlyTrend?.expensesByCategory?.[index]?.categories || [];

        return {
          month,
          opex: opexValue,
          categories: categoriesData,
          expenseDetails: categoriesData,
        };
      },
    );

    const hasExpensesByCategory = monthlyBreakdown.some(
      (m) => m.categories && m.categories.length > 0,
    );

    // Find best and worst months (best = lowest OpEx, worst = highest OpEx)
    const bestMonth = monthlyBreakdown.reduce(
      (best, current) =>
        current.opex < (best?.opex || Infinity) ? current : best,
      monthlyBreakdown[0],
    );
    const worstMonth = monthlyBreakdown.reduce(
      (worst, current) => (current.opex > (worst?.opex || 0) ? current : worst),
      monthlyBreakdown[0],
    );

    const totalCategories = hasExpensesByCategory
      ? [
          ...new Set(
            monthlyBreakdown.flatMap((m) => m.categories.map((c) => c.name)),
          ),
        ].length
      : 0;

    setDrillDown({
      open: true,
      title: "Operating Expenses - Monthly Breakdown",
      subtitle: `${monthlyBreakdown.length} months • ${formatNullableCurrency(data.operatingExpenses)} total`,
      description: (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            Operating Expenses by Month & Category
          </Typography>
          <Typography variant="body2" paragraph>
            Monthly breakdown of operating expenses (OpEx) - the costs of
            running your business including salaries, rent, marketing,
            technology, and other overhead.{" "}
            {hasExpensesByCategory
              ? "Click on a month to see expense breakdown by category with detailed tooltips showing individual expenses."
              : ""}
          </Typography>
        </Box>
      ),
      data: monthlyBreakdown,
      useBarChart: hasExpensesByCategory, // Enable bar chart when we have category data
      columns: [
        { field: "month", headerName: "Month", width: 200 },
        {
          field: "opex",
          headerName: "Total OpEx",
          width: 200,
          renderCell: (row) =>
            `$${row.opex?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
      ],
      summary: {
        metrics: [
          {
            label: "Total OpEx",
            value: formatNullableCurrency(data.operatingExpenses),
            subtitle: data.expensesComparable === false ? "Company-wide — not attributable to the filtered subset" : undefined,
            color: "error.main",
          },
          {
            label: "Avg Monthly OpEx",
            value: formatNullableCurrency(
              data.operatingExpenses === null || data.operatingExpenses === undefined
                ? null
                : data.operatingExpenses / (monthlyBreakdown.length || 1),
            ),
          },
          { label: "Total Categories", value: totalCategories },
          {
            label: "OpEx/Revenue Ratio",
            value:
              data.operatingExpenses === null ||
              data.operatingExpenses === undefined ||
              !data.totalRevenue
                ? "n/a"
                : `${((data.operatingExpenses / data.totalRevenue) * 100).toFixed(1)}%`,
          },
          {
            label: "Best Month (Lowest)",
            value: `${bestMonth?.month} • $${bestMonth?.opex?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "success.main",
          },
          {
            label: "Worst Month (Highest)",
            value: `${worstMonth?.month} • $${worstMonth?.opex?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "error.main",
          },
        ],
      },
    });
  }, []);

  const handleNetProfitClick = useCallback((data) => {
    // Transform monthlyTrend object into breakdown array
    const monthlyBreakdown = (data.monthlyTrend?.months || []).map(
      (month, index) => ({
        month,
        revenue: data.monthlyTrend?.revenue?.[index] || 0,
        costs: data.monthlyTrend?.costs?.[index] || 0,
        opex: data.monthlyTrend?.opex?.[index] || 0,
        netProfit:
          (data.monthlyTrend?.revenue?.[index] || 0) -
          (data.monthlyTrend?.costs?.[index] || 0) -
          (data.monthlyTrend?.opex?.[index] || 0),
        netMargin:
          (((data.monthlyTrend?.revenue?.[index] || 0) -
            (data.monthlyTrend?.costs?.[index] || 0) -
            (data.monthlyTrend?.opex?.[index] || 0)) /
            (data.monthlyTrend?.revenue?.[index] || 1)) *
          100,
      }),
    );

    setDrillDown({
      open: true,
      title: "Net Profit - Monthly Breakdown",
      subtitle: `${monthlyBreakdown.length} months • $${data.netProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total`,
      description: (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            Bottom Line Performance by Month
          </Typography>
          <Typography variant="body2" paragraph>
            Monthly breakdown of Net Profit - your true profitability after ALL
            expenses (both direct costs and operating expenses). This is the
            money available for reinvestment, distributions, and growth.
            Consistent positive net profit indicates a sustainable, healthy
            business.
          </Typography>
        </Box>
      ),
      data: monthlyBreakdown,
      columns: [
        { field: "month", headerName: "Month", width: 130 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 140,
          renderCell: (row) =>
            `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "costs",
          headerName: "Costs",
          width: 130,
          renderCell: (row) =>
            `$${row.costs?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "opex",
          headerName: "OpEx",
          width: 130,
          renderCell: (row) =>
            `$${row.opex?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "netProfit",
          headerName: "Net Profit",
          width: 140,
          renderCell: (row) =>
            `$${(row.revenue - row.costs - row.opex)?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "netMargin",
          headerName: "Net Margin",
          width: 120,
          renderCell: (row) =>
            `${(((row.revenue - row.costs - row.opex) / row.revenue) * 100)?.toFixed(1)}%`,
        },
      ],
      summary: {
        metrics: [
          {
            label: "Total Net Profit",
            value: formatNullableCurrency(data.netProfit),
            subtitle:
              data.expensesComparable === false
                ? "Not applicable — expense allocation is company-wide"
                : undefined,
            color:
              data.netProfit === null || data.netProfit === undefined
                ? "text.secondary"
                : data.netProfit >= 0
                  ? "success.main"
                  : "error.main",
          },
          {
            label: "Net Margin",
            value: formatPercentValue(data.netProfitMargin, { digits: 1 }),
            color:
              data.netProfitMargin === null ||
              data.netProfitMargin === undefined
                ? "text.secondary"
                : data.netProfitMargin >= 20
                  ? "success.main"
                  : data.netProfitMargin >= 10
                    ? "warning.main"
                    : "error.main",
          },
          {
            label: "Avg Monthly",
            value: formatNullableCurrency(
              data.netProfit === null || data.netProfit === undefined
                ? null
                : data.netProfit / (monthlyBreakdown.length || 1),
            ),
          },
          {
            label: "Total Revenue",
            value: `$${data.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
        ],
      },
    });
  }, []);

  const handleServiceMixClick = useCallback(
    async (category, data) => {
      try {
        // Fetch detailed services for this category
        const queryParams = {
          ...filters,
          category: category.label, // "Translation", "Interpretation", or "Other Services"
        };

        const response = await api.get(
          "/reports/financial/services-by-category",
          { params: queryParams },
        );
        const servicesData = response.data.data;

        const categoryMap = {
          Translation: "Translation Services",
          Interpretation: "Interpretation Services",
          Other: "Other Services",
          "Other Services": "Other Services",
        };

        setDrillDown({
          open: true,
          title: `${categoryMap[category.label]} - Service Details`,
          subtitle: `${servicesData.summary.count} services • $${category.value?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} revenue`,
          description: (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
              >
                Individual Service Breakdown
              </Typography>
              <Typography variant="body2" paragraph>
                Detailed list of all {category.label.toLowerCase()} services
                delivered during this period. View dates,clients, linguists, and
                profitability for each service. This helps identify which
                specific services and clients are driving revenue and which are
                most profitable.
              </Typography>
              {servicesData.summary && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: "background.alt",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" display="block">
                    <strong>Total Services:</strong>{" "}
                    {servicesData.summary.count}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Total Revenue:</strong> $
                    {servicesData.summary.totalRevenue?.toLocaleString(
                      undefined,
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                    )}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Total Profit:</strong> $
                    {servicesData.summary.totalProfit?.toLocaleString(
                      undefined,
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                    )}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Avg Margin:</strong>{" "}
                    {servicesData.summary.avgMargin}%
                  </Typography>
                </Box>
              )}
            </Box>
          ),
          data: servicesData.services || [],
          columns: [
            {
              field: "date",
              headerName: "Date",
              width: 110,
              renderCell: (row) =>
                new Date(row.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }),
            },
            { field: "clientName", headerName: "Client", width: 200 },
            { field: "linguistName", headerName: "Linguist", width: 150 },
            { field: "description", headerName: "Description", width: 250 },
            {
              field: "AccountCharge",
              headerName: "Revenue",
              width: 120,
              renderCell: (row) =>
                `$${row.AccountCharge?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
              field: "TotalCost",
              headerName: "Cost",
              width: 120,
              renderCell: (row) =>
                `$${row.TotalCost?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
              field: "Profit",
              headerName: "Profit",
              width: 120,
              renderCell: (row) =>
                `$${row.Profit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
              field: "Margin",
              headerName: "Margin %",
              width: 100,
              renderCell: (row) => `${row.Margin?.toFixed(1)}%`,
            },
          ],
          summary: servicesData.summary
            ? {
                metrics: [
                  {
                    label: "Total Services",
                    value: servicesData.summary.count,
                  },
                  {
                    label: "Total Revenue",
                    value: `$${servicesData.summary.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    color: "success.main",
                  },
                  {
                    label: "Total Cost",
                    value: `$${servicesData.summary.totalCost?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    color: "error.main",
                  },
                  {
                    label: "Total Profit",
                    value: `$${servicesData.summary.totalProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    color: "info.main",
                  },
                  {
                    label: "Avg Margin",
                    value: `${servicesData.summary.avgMargin}%`,
                  },
                ],
              }
            : null,
        });
      } catch (error) {
        console.error("Error fetching service details:", error);
      }
    },
    [filters],
  );

  const metricsConfig = useCallback(
    (data) => {
      // Generate dynamic YoY insight
      const yoyInsight =
        data.yoyGrowth != null
          ? data.yoyGrowth > 0
            ? `\n\n📈 Revenue is ${Math.abs(data.yoyGrowth).toFixed(2)}% HIGHER than the same period last year, indicating growth.`
            : data.yoyGrowth < 0
              ? `\n\n📉 Revenue is ${Math.abs(data.yoyGrowth).toFixed(2)}% LOWER than the same period last year, indicating a decline.`
              : `\n\n➡️ Revenue is unchanged compared to the same period last year.`
          : "";

      return [
        {
          groupTitle: "Profit & Loss Summary",
          metrics: [
            {
              title: "Total Revenue",
              value: `$${data.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              trend: data.yoyGrowth,
              trendLabel: "YoY growth",
              tooltipTitle: "Total Revenue & YoY Growth",
              tooltipContent: `Gross revenue from all client charges (AccountCharge) in the selected period. YoY (Year-over-Year) growth compares this period's revenue to the same period last year.${yoyInsight}`,
              onClick: () => handleTotalRevenueClick(data),
            },
            {
              title: "Cost of Revenue",
              value: `$${data.costOfRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: `${(100 - (data.grossProfitMargin || 0)).toFixed(2)}% of revenue`,
              color: "error.main",
              tooltipTitle: "Cost of Revenue",
              tooltipContent:
                "This represents direct costs incurred to deliver your services. This includes:\n\n• Linguist payments (interpreter/translator fees)\n• Direct service delivery costs\n• Third-party vendor costs for fulfilling services\n\nThese costs are directly tied to each service delivered - if you don't provide a service, you don't incur these costs. This is subtracted from Total Revenue to calculate Gross Profit.\n\nWhy it matters: Lower cost of revenue (while maintaining quality) means higher profitability per service.",
              onClick: () => handleCostOfRevenueClick(data),
            },
            {
              title: "Gross Profit",
              value: `$${data.grossProfit?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: `${data.grossProfitMargin?.toFixed(2)}% margin`,
              color:
                data.grossProfitMargin >= 50
                  ? "success.main"
                  : data.grossProfitMargin >= 30
                    ? "warning.main"
                    : "error.main",
              tooltipTitle: "Gross Profit & Margin",
              tooltipContent:
                "Gross Profit = Total Revenue - Cost of Revenue\n\nThis shows how much money you make after paying for direct service delivery costs, but BEFORE paying for business operations (rent, salaries, marketing, etc.).\n\nGross Profit Margin % = (Gross Profit ÷ Total Revenue) × 100\n\nBenchmarks for language service businesses:\n🟢 Excellent: 50-60% margin\n🟡 Good: 40-49% margin\n🟠 Acceptable: 30-39% margin\n🔴 Concerning: Below 30% margin\n\nWhy it matters: This tells you if your pricing is adequate relative to your service delivery costs. Low margins may indicate you need to raise prices or negotiate better linguist rates.",
              onClick: () => handleGrossProfitClick(data),
            },
            {
              title: "Operating Expenses",
              value: formatNullableCurrency(data.operatingExpenses),
              subtitle:
                data.expensesComparable === false
                  ? "Company-wide — not attributable to the filtered subset"
                  : "Business expenses",
              color: "error.main",
              tooltipTitle: "Operating Expenses (OpEx)",
              tooltipContent:
                "Operating expenses are the ongoing costs to run your business that are NOT directly tied to delivering a specific service. Common categories include:\n\n• Salaries & Benefits (office staff, managers, sales team)\n• Rent & Utilities (office space, internet, phone)\n• Technology & Software (subscriptions, licenses, IT support)\n• Marketing & Advertising\n• Professional Services (legal, accounting)\n• Insurance & Taxes\n• Office Supplies & Equipment\n\nThese expenses occur whether you deliver 1 service or 100 services.\n\nWhy it matters: Operating expenses should grow slower than revenue - if revenue doubles but OpEx also doubles, you're not scaling efficiently. Track the OpEx-to-Revenue ratio to ensure expenses don't grow faster than income.",
              onClick: () => handleOperatingExpensesClick(data),
            },
            {
              title: "Net Profit",
              value: formatNullableCurrency(data.netProfit),
              subtitle:
                data.expensesComparable === false
                  ? "Not applicable — expense allocation is company-wide"
                  : formatPercentValue(data.netProfitMargin, { digits: 2 }) +
                    " net margin",
              color:
                data.netProfit === null || data.netProfit === undefined
                  ? "text.secondary"
                  : data.netProfit >= 0
                    ? "success.main"
                    : "error.main",
              tooltipTitle: "Net Profit (Bottom Line)",
              tooltipContent:
                "Net Profit = Gross Profit - Operating Expenses\n\nThis is THE most important number - your actual profit after ALL costs (both service delivery and business operations). This is the money available for:\n• Business reinvestment\n• Emergency reserves\n• Owner distributions/dividends\n• Growth initiatives\n\nNet Profit Margin % = (Net Profit ÷ Total Revenue) × 100\n\nBenchmarks for service businesses:\n🟢 Excellent: 20-30% margin\n🟡 Good: 15-19% margin\n🟠 Acceptable: 10-14% margin\n🔴 Concerning: Below 10% margin\n\nWhy it matters: This tells you if your business is truly profitable after all expenses. Negative net profit means you're losing money. Consistently low margins mean there's little cushion for unexpected costs or economic downturns.",
              onClick: () => handleNetProfitClick(data),
            },
          ],
        },
        {
          groupTitle: "Core Revenue Metrics",
          metrics: [
            {
              title: "Avg Monthly Revenue",
              value: `$${data.avgMonthlyRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: `±$${data.revenueStdDev?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"} std dev`,
              tooltipTitle: "Average Monthly Revenue & Volatility",
              tooltipContent:
                "Avg Monthly Revenue = Total Revenue ÷ Number of Months\n\nThis shows your typical monthly income, which helps with budgeting and forecasting.\n\nStandard Deviation (Std Dev) measures revenue consistency:\n• LOW std dev = Predictable, stable revenue month-to-month\n• HIGH std dev = Volatile, unpredictable revenue (big swings)\n\nExample: If avg is $50K with ±$5K std dev, expect most months between $45K-$55K. But ±$25K std dev means months could range from $25K-$75K.\n\nWhy it matters: Predictable revenue (low std dev) makes cash flow planning easier. High volatility means you need larger cash reserves to handle lean months. If you see high volatility, investigate whether it's seasonal patterns or client concentration risk.",
            },
            {
              title: "Avg Service Charge",
              value: `$${data.avgServiceCharge?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: "Per service",
              tooltipTitle: "Average Service Charge (Avg Transaction Size)",
              tooltipContent:
                "Avg Service Charge = Total Revenue ÷ Total Number of Services\n\nThis shows how much you charge on average for each service delivered, across all service types (interpretation, translation, etc.).\n\nHow to use this metric:\n• Track over time - is it increasing (good, means higher-value services) or decreasing (may indicate price erosion)?\n• Compare to your costs - if avg charge is $200 and avg cost is $150, you have $50 gross profit per service\n• Identify opportunities - can you upsell clients to higher-value services to increase this number?\n\nWhy it matters: Increasing your average service charge (through better pricing, premium services, or service mix) is often easier than acquiring more clients. Even a 10% increase in avg charge directly increases revenue by 10% without additional marketing costs.",
            },
          ],
        },
        {
          groupTitle: "Growth & Performance",
          metrics: [
            {
              title: "Revenue Velocity",
              value: `${data.revenueVelocity?.toFixed(2) || "0.00"}%`,
              subtitle: "Monthly growth rate",
              trend: data.revenueVelocity,
              trendLabel: "acceleration",
              tooltipTitle: "Revenue Velocity (Growth Rate)",
              tooltipContent:
                "Revenue velocity measures how fast your revenue is growing on a month-to-month basis - basically your growth rate.\n\nHow to interpret:\n🟢 Positive velocity (e.g., +5%) = Revenue is ACCELERATING, growing faster each month\n🟡 Zero or near-zero = Revenue is STABLE, growing at a consistent pace\n🔴 Negative velocity (e.g., -3%) = Revenue is DECELERATING, growth is slowing\n\nExample: If you grew 10% in January, 12% in February, and 15% in March, you have positive velocity - your growth rate itself is increasing.\n\nWhy it matters: This is an early warning indicator. Negative velocity doesn't mean revenue is declining (you may still be growing), but growth is slowing down. This could signal market saturation, increased competition, or need for new growth initiatives. Positive velocity indicates momentum and healthy expansion.",
            },
          ],
        },
        {
          groupTitle: "Service Mix",
          metrics: [
            {
              title: "Translation Revenue",
              value: `$${data.translationRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle:
                data.totalRevenue > 0
                  ? `${sharePercent(data.translationRevenue, data.totalRevenue)} of total`
                  : "0.00% of total",
              tooltipTitle: "Translation Service Revenue",
              tooltipContent:
                "Revenue from document translation services (written translation work).\n\nTranslation services typically have different characteristics than interpretation:\n• Often higher per-project value\n• Longer turnaround times\n• More predictable scheduling\n• Can be done remotely by linguists anywhere\n• Usually priced per word, page, or project\n\nWhy track this separately: Understanding your service mix helps with:\n• Resource planning (translator vs interpreter recruitment)\n• Pricing strategy optimization\n• Identifying which services drive growth\n• Diversification assessment - relying too heavily on one service type creates risk\n\nHealthy businesses often have a balanced portfolio across service types.",
              onClick: () =>
                handleServiceMixClick(
                  { label: "Translation", value: data.translationRevenue },
                  data,
                ),
            },
            {
              title: "Interpretation Revenue",
              value: `$${data.interpretationRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle:
                data.totalRevenue > 0
                  ? `${sharePercent(data.interpretationRevenue, data.totalRevenue)} of total`
                  : "0.00% of total",
              tooltipTitle: "Interpretation Service Revenue",
              tooltipContent:
                "Revenue from all interpretation services including:\n• OPI (Over-the-Phone Interpretation)\n• VRI (Video Remote Interpretation)\n• On-Site (In-person interpretation)\n\nInterpretation services have unique characteristics:\n• Usually immediate or scheduled appointments\n• Priced by hour or minute\n• Requires real-time linguist availability\n• On-site interpretation typically has highest rates\n• Phone/video interpretation allows for faster scaling\n\nWhy track this separately: Different service types have different margins, delivery costs, and growth potential. For example, on-site interpretation may be more profitable but harder to scale than phone interpretation. Understanding the breakdown helps you make strategic decisions about where to invest in growth.",
              onClick: () =>
                handleServiceMixClick(
                  {
                    label: "Interpretation",
                    value: data.interpretationRevenue,
                  },
                  data,
                ),
            },
            {
              title: "Other Services Revenue",
              value: `$${data.otherServicesRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle:
                data.totalRevenue > 0
                  ? `${sharePercent(data.otherServicesRevenue, data.totalRevenue)} of total`
                  : "0.00% of total",
              tooltipTitle: "Other Services Revenue",
              tooltipContent:
                "Revenue from services beyond core translation and interpretation, such as:\n• Localization\n• Transcription\n• Subtitling/Captioning\n• Cultural Consulting\n• Language Training\n• Voiceover Services\n• Desktop Publishing\n\nWhy this matters: Other services can:\n• Increase revenue per client (cross-selling opportunities)\n• Differentiate you from competitors\n• Provide more stable revenue streams\n• Command premium pricing for specialized expertise\n\nGrowing 'Other Services' revenue indicates successful diversification. If this number is very low, consider what complementary services your clients might need that you could offer. Ask your top clients what other language-related challenges they face.",
              onClick: () =>
                handleServiceMixClick(
                  { label: "Other", value: data.otherServicesRevenue },
                  data,
                ),
            },
          ],
        },
      ];
    },
    [
      handleTotalRevenueClick,
      handleCostOfRevenueClick,
      handleGrossProfitClick,
      handleOperatingExpensesClick,
      handleNetProfitClick,
      handleServiceMixClick,
    ],
  );

  const chartsConfig = useCallback((data) => {
    // Drill-down handlers
    const handleClientClick = (item) => {
      const clientData = data.topClients || [];
      setDrillDown({
        open: true,
        title: "All Clients by Revenue",
        subtitle: "Complete breakdown of revenue by client",
        description:
          "This is your complete client portfolio ranked by revenue from highest to lowest. Use this view to understand revenue distribution, identify growth opportunities, and assess client concentration risk. Each client's contribution to total revenue is shown as both a dollar amount and percentage. Look for patterns: Are top clients growing? Is revenue well distributed or concentrated? Are any clients declining year-over-year?",
        data: clientData,
        columns: [
          { field: "fullName", headerName: "Client Name", width: 300 },
          {
            field: "revenue",
            headerName: "Revenue",
            width: 150,
            renderCell: (row) =>
              `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
          { field: "servicesCount", headerName: "Services", width: 100 },
          {
            field: "percentOfTotal",
            headerName: "% of Total",
            width: 120,
            renderCell: (row) => `${row.percentOfTotal?.toFixed(2)}%`,
          },
        ],
        summary: {
          metrics: [
            { label: "Total Clients", value: clientData.length },
            {
              label: "Total Revenue",
              value: `$${data.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
              label: "Avg per Client",
              value: `$${(data.totalRevenue / clientData.length).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
          ],
        },
      });
    };

    const handleLinguistClick = (item) => {
      const linguistData = data.topLinguists || [];
      setDrillDown({
        open: true,
        title: "All Linguists by Revenue",
        subtitle: "Complete breakdown of revenue by linguist",
        description:
          "This shows all active linguists ranked by the revenue they generated through completed services. High revenue linguists are your most productive providers and critical to operations. Monitor this to ensure fair work distribution, identify retention risks (are top performers heading toward burnout?), and recognize exceptional contributors. The 'Avg/Service' column shows efficiency - some linguists may do fewer jobs but at higher rates (specialized work), while others have high volume at lower rates.",
        data: linguistData,
        columns: [
          { field: "name", headerName: "Linguist Name", width: 200 },
          {
            field: "revenue",
            headerName: "Revenue",
            width: 150,
            renderCell: (row) =>
              `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
          { field: "servicesCount", headerName: "Services", width: 100 },
          {
            field: "avgPerService",
            headerName: "Avg/Service",
            width: 120,
            renderCell: (row) =>
              `$${row.avgPerService?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
        ],
        summary: {
          metrics: [
            { label: "Total Linguists", value: linguistData.length },
            {
              label: "Total Services",
              value: linguistData.reduce((sum, l) => sum + l.servicesCount, 0),
            },
            {
              label: "Avg Revenue",
              value: `$${(linguistData.reduce((sum, l) => sum + l.revenue, 0) / linguistData.length).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
          ],
        },
      });
    };

    const handleConcentrationClick = (item) => {
      if (item.id === 1) {
        // Top 20% clients
        const top20Clients = (data.topClients || []).slice(
          0,
          Math.ceil((data.topClients?.length || 0) * 0.2),
        );
        setDrillDown({
          open: true,
          title: "Top 20% Clients",
          subtitle: "High-value clients contributing most revenue",
          description:
            "These are your MOST CRITICAL clients - the top 20% by revenue who likely generate 60-80% of your total income (Pareto Principle). Losing even ONE of these clients would significantly impact your business. These relationships deserve maximum investment: dedicated account managers, quarterly business reviews, priority response times, and proactive service. Monitor their satisfaction closely, understand their strategic goals, and ensure they see you as an indispensable partner, not just a vendor. What do these top clients have in common? Use those insights to target similar prospects.",
          data: top20Clients,
          columns: [
            { field: "fullName", headerName: "Client Name", width: 300 },
            {
              field: "revenue",
              headerName: "Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
            {
              field: "percentOfTotal",
              headerName: "% of Total",
              width: 120,
              renderCell: (row) => `${row.percentOfTotal?.toFixed(2)}%`,
            },
          ],
          summary: {
            metrics: [
              { label: "Client Count", value: top20Clients.length },
              {
                label: "Total Revenue",
                value: `$${data.top20ClientsRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "Concentration",
                value: `${data.concentrationRatio?.toFixed(2)}%`,
              },
            ],
          },
        });
      } else {
        // Other 80% clients
        const other80Clients = (data.topClients || []).slice(
          Math.ceil((data.topClients?.length || 0) * 0.2),
        );
        setDrillDown({
          open: true,
          title: "Other 80% Clients",
          subtitle: "Remaining client base",
          description:
            "These clients represent the remaining 80% of your client count but typically generate only 20-40% of total revenue (Pareto Principle). While individually smaller, collectively they're important for revenue stability and diversification. Evaluate this segment for: (1) Growth potential - can any be developed into top-tier clients with better service or account management? (2) Profitability - are smaller clients profitable at current service levels, or do they require disproportionate administrative effort? (3) Strategic value - some may be small now but have high growth potential or provide valuable industry diversity.",
          data: other80Clients,
          columns: [
            { field: "fullName", headerName: "Client Name", width: 300 },
            {
              field: "revenue",
              headerName: "Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
            {
              field: "percentOfTotal",
              headerName: "% of Total",
              width: 120,
              renderCell: (row) => `${row.percentOfTotal?.toFixed(2)}%`,
            },
          ],
          summary: {
            metrics: [
              { label: "Client Count", value: other80Clients.length },
              {
                label: "Total Revenue",
                value: `$${((data.totalRevenue || 0) - (data.top20ClientsRevenue || 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "% of Total",
                value: `${(100 - data.concentrationRatio)?.toFixed(2)}%`,
              },
            ],
          },
        });
      }
    };

    // Only show Seasonality Index for full year or multi-year periods
    const showSeasonality =
      data.periodMetadata?.type === "single-year" ||
      data.periodMetadata?.type === "multi-year";

    return (
      <Grid container spacing={3}>
        {/* Revenue Trend with Moving Averages */}
        <Grid size={{ xs: 12, lg: showSeasonality ? 8 : 12 }}>
          {(data.monthlyTrend?.months?.length || 0) < 2 ? (
            <Paper
              sx={{
                p: 3,
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.primary.border2}`,
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}
              >
                <Typography variant="h6" fontWeight="600">
                  Revenue Trend (Last 6 Months)
                </Typography>
                <HtmlTooltip
                  title={
                    <Box sx={{ p: 1, maxHeight: "400px", overflowY: "auto" }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Revenue Trend Analysis
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        {`This chart shows your monthly revenue momentum and growth patterns:

• Current Year (solid line): This period's actual monthly revenue
• Last Year Same Period (gray line): Baseline for year-over-year comparison - are you growing?
• 3-Month Rolling Average (trend line): Smooths daily/weekly volatility to show the true underlying trend direction

How to read it:
• Lines trending upward = Growth
• Lines trending downward = Decline
• Gap between current and last year = YoY growth/decline
• If rolling average is rising even when monthly revenue fluctuates, your business is healthy

Note: Only displays the last 6 months to focus on recent trends, regardless of your selected date range.`}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="bottom-start"
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 18,
                      color: theme.palette.primary.main,
                      cursor: "help",
                      opacity: 0.7,
                      "&:hover": {
                        opacity: 1,
                      },
                    }}
                  />
                </HtmlTooltip>
              </Box>
              <Box sx={{ textAlign: "center", maxWidth: 500 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Insufficient data for trend analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The Revenue Trend chart requires at least 2 months of data to
                  display meaningful trends. Please select a broader date range
                  (e.g., "Last 6 Months" or "This Year") to view revenue trends
                  over time.
                </Typography>
              </Box>
            </Paper>
          ) : (
            <ReportLineChart
              title="Revenue Trend (Last 6 Months)"
              tooltipTitle="Revenue Trend Analysis"
              tooltipContent={`This chart shows your monthly revenue momentum and growth patterns:

• Current Year (solid line): This period's actual monthly revenue
• Last Year Same Period (gray line): Baseline for year-over-year comparison - are you growing?
• 3-Month Rolling Average (trend line): Smooths daily/weekly volatility to show the true underlying trend direction

How to read it:
• Lines trending upward = Growth
• Lines trending downward = Decline
• Gap between current and last year = YoY growth/decline
• If rolling average is rising even when monthly revenue fluctuates, your business is healthy

Note: Only displays the last 6 months to focus on recent trends, regardless of your selected date range.`}
              xAxisData={data.monthlyTrend?.months || []}
              series={[
                {
                  data: data.monthlyTrend?.revenue || [],
                  label: "Current Year",
                  curve: "monotoneX",
                  color: chartColors.semantic.primary, // Primary data
                },
                {
                  data: data.monthlyTrend?.lastYearRevenue || [],
                  label: "Last Year (Same Period)",
                  curve: "monotoneX",
                  color: theme.palette.grey[500], // Historical comparison
                },
                {
                  data: data.monthlyTrend?.movingAvg3Month || [],
                  label: "3-Month Rolling Avg",
                  curve: "monotoneX",
                  color: chartColors.semantic.secondary, // Trend indicator
                },
              ]}
              height={400}
              valueFormatter={(value) =>
                value
                  ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : ""
              }
            />
          )}
        </Grid>

        {/* Seasonality Index - Only show for full year or multi-year periods */}
        {showSeasonality && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <ReportBarChart
              title="Seasonality Index"
              tooltipTitle="Quarterly Revenue Patterns (Seasonality)"
              tooltipContent={`The Seasonality Index reveals if certain quarters consistently perform better or worse than others.

How to read it:
• 100 = Average quarter performance (baseline)
• Above 100 = Above-average quarter (e.g., 120 = 20% above average)
• Below 100 = Below-average quarter (e.g., 80 = 20% below average)

Example: If Q4 shows 130, you typically generate 30% more revenue in Q4 than your average quarter - possibly due to year-end budget spending.

Why this matters:
• Plan cash flow - save during high-revenue quarters for low-revenue periods
• Staff accordingly - hire temporary help during peak quarters
• Set realistic targets - don't expect Q1 performance if it's historically slow
• Time major expenses for high-revenue quarters

Note: Only shown when analyzing a full year or more to calculate meaningful patterns.`}
              data={[
                { quarter: "Q1", index: data.seasonalityIndex?.Q1 || 100 },
                { quarter: "Q2", index: data.seasonalityIndex?.Q2 || 100 },
                { quarter: "Q3", index: data.seasonalityIndex?.Q3 || 100 },
                { quarter: "Q4", index: data.seasonalityIndex?.Q4 || 100 },
              ]}
              xAxisKey="quarter"
              series={[
                {
                  dataKey: "index",
                  label: "Index (100 = avg)",
                },
              ]}
              height={400}
              showLegend={false}
              valueFormatter={(value) => (value != null ? `${value}` : "0")}
            />
          </Grid>
        )}

        {/* Top 10 Clients */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Top 5 Clients by Revenue"
            tooltipTitle="Top Revenue Generators (Your VIP Clients)"
            tooltipContent={`Your top 5 revenue-generating clients - these are the relationships that drive your business and deserve VIP treatment.

Why this chart matters:
• VISIBILITY: Quick at-a-glance view of who your most important clients are
• DEPENDENCY CHECK: If one bar dominates, you have concentration risk
• RELATIONSHIP PRIORITY: These clients should get your best service, fastest response times, and most attention
• SUCCESS PATTERNS: What do they have in common? Use this to find more clients like them

What to look for:
• Is the gap between #1 and #2 huge? (High concentration risk)
• Are your top clients growing or flat year-over-year?
• Do you have good relationships with decision-makers at these companies?
• Are any of these at risk of leaving? (Would devastate revenue)

Best practices for top clients:
1. Assign dedicated account managers
2. Schedule quarterly business review meetings
3. Respond to requests within 1 hour (not 24 hours)
4. Proactively suggest new services that could help them
5. Send thank-you gifts and personalized communications
6. Ask for feedback regularly and act on it immediately

Click any bar to drill down and see ALL clients ranked by revenue with full details.`}
            data={
              data.topClients?.slice(0, 5).map((c) => ({
                name: c.name,
                fullName: c.fullName,
                revenue: c.revenue,
                services: c.servicesCount,
              })) || []
            }
            xAxisKey="name"
            tooltipKey="fullName"
            series={[
              {
                dataKey: "revenue",
                label: "Revenue",
                color: chartColors.semantic.primary, // Primary metric
              },
            ]}
            height={350}
            valueFormatter={(value) =>
              value != null
                ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "$0.00"
            }
            onBarClick={handleClientClick}
          />
        </Grid>

        {/* Top 10 Linguists */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Top 5 Linguists by Revenue"
            tooltipTitle="Top Revenue-Generating Linguists (Your MVPs)"
            tooltipContent={`Your top 5 revenue-generating linguists - these are your Most Valuable Providers (MVPs) who drive the most business.

Why this chart matters:
• TALENT RETENTION: These linguists are critical to your operations - ensure they're happy and engaged
• CAPACITY PLANNING: If you're dependent on a few linguists, what happens when they're unavailable?
• FAIR DISTRIBUTION: Is work too concentrated? Other linguists may feel underutilized
• QUALITY INDICATORS: High revenue usually correlates with client satisfaction and repeat requests

What to look for:
• Is one linguist doing a disproportionate amount of work? (Burnout risk + dependency)
• Are top linguists getting competitive pay rates? (Retention risk)
• Do they work with multiple clients or just one? (Flexibility indicator)
• Are they at capacity? (May need to recruit similar talent)

Best practices for top linguists:
1. Priority scheduling - give them first choice of assignments
2. Competitive or premium pay rates to retain them
3. Regular check-ins to ensure satisfaction
4. Professional development opportunities
5. Recognition and appreciation (public praise, bonuses, awards)
6. Clear communication about upcoming work pipeline
7. Involve them in training newer linguists

Risk mitigation: While you want to leverage your best talent, ensure you're developing a deeper bench. What if your #1 linguist leaves tomorrow?

Click any bar to see ALL linguists ranked by revenue with detailed performance metrics.`}
            data={
              data.topLinguists?.slice(0, 5).map((l) => ({
                name: l.name,
                revenue: l.revenue,
                services: l.servicesCount,
              })) || []
            }
            xAxisKey="name"
            series={[
              {
                dataKey: "revenue",
                label: "Revenue",
                color: chartColors.semantic.secondary, // Secondary metric
              },
            ]}
            height={350}
            valueFormatter={(value) =>
              value != null
                ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "$0.00"
            }
            onBarClick={handleLinguistClick}
          />
        </Grid>

        {/* Revenue Concentration */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Revenue Concentration Analysis"
            tooltipTitle="80/20 Rule Analysis (Pareto Principle)"
            tooltipContent={`This chart applies the famous Pareto Principle to your client base, showing what percentage of revenue comes from your top 20% of clients versus the other 80%.

The Pareto Principle (discovered by economist Vilfredo Pareto):
'80% of outcomes come from 20% of causes'

In business, this typically means:
• 80% of revenue comes from 20% of clients
• 80% of complaints come from 20% of clients
• 80% of profits come from 20% of products

What this chart reveals:
If the TOP 20% slice is very large (>80% of revenue):
🔴 HIGH RISK: You're highly dependent on a few key relationships. Losing even one could be devastating.
+ Action: Invest heavily in retaining these clients + diversify client base

If the TOP 20% slice is moderate (60-75% of revenue):
🟡 BALANCED: Healthy concentration with some diversification
+ Action: Standard account management practices

If the TOP 20% slice is small (<60% of revenue):
🟢 DIVERSIFIED: Revenue well distributed across client base
+ Action: Identify if you can grow any clients into larger accounts

Why concentration happens:
• Natural market dynamics (some clients simply need more services)
• Better relationships with certain clients
• Industry-specific factors (healthcare vs. legal clients may have different volume needs)

Strategic implications:
• High concentration = Higher profit margins (easier to serve fewer clients well) BUT higher risk
• Low concentration = Lower risk BUT potentially lower margins (more overhead managing many small accounts)

Click either slice to see the detailed client list with revenue breakdown and strategic recommendations.`}
            onSliceClick={(item) => {
              handleConcentrationClick(item);
            }}
            data={[
              {
                id: 1,
                label: "Top 20% Clients",
                value: data.top20ClientsRevenue || 0,
              },
              {
                id: 2,
                label: "Other 80% Clients",
                value:
                  (data.totalRevenue || 0) - (data.top20ClientsRevenue || 0),
              },
            ]}
            height={350}
            showLegend={true}
            valueFormatter={(value) =>
              value != null
                ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "$0.00"
            }
          />
        </Grid>

        {/* Service Type Distribution */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Revenue by Service Type"
            tooltipTitle="Service Mix Breakdown (Portfolio Diversification)"
            tooltipContent={`This chart shows what percentage of your revenue comes from each major service category, helping you understand your service portfolio balance.

Why service mix matters:

1. RISK MANAGEMENT:
   • Over-reliance on one service type creates vulnerability
   • Example: If 90% is translation and demand shifts to interpretation, you're exposed
   • Diversified service mix = more stable, resilient business

2. CAPACITY PLANNING:
   • Translation: Requires translators, often project-based, can be done remotely
   • Interpretation: Requires interpreters, often scheduled or on-demand, real-time delivery
   • Each service type needs different resources and infrastructure

3. PROFITABILITY INSIGHTS:
   • Different services have different margins
   • On-site interpretation: Often highest revenue per hour, but travel costs
   • Phone/video interpretation: Lower per-minute rate, but highly scalable
   • Translation: Project-based pricing, can have high margins for specialized work

4. MARKET POSITIONING:
   • Are you known as a translation company? Interpretation company? Full-service?
   • Your service mix shapes your brand and marketing message

5. GROWTH OPPORTUNITIES:
   • If one service dominates, can you cross-sell others to existing clients?
   • Example: If client uses translation, do they know you offer interpretation too?

Healthy service portfolios often show:
• No single service type over 70% (diversification)
• Growth in 'Other Services' indicates successful expansion
• Balance that matches your strategic goals (specialization vs. diversification)

Red flags:
• 90%+ from one service = High dependency, limited growth options
• Declining share in core services = Possible competitive pressure

Action items:
• Identify which service types have highest margins - can you grow those?
• Survey clients about unmet needs in other service areas
• Train existing linguists to offer multiple service types (cross-training)`}
            data={[
              {
                id: 1,
                label: "Translation",
                value: data.translationRevenue || 0,
              },
              {
                id: 2,
                label: "Interpretation",
                value: data.interpretationRevenue || 0,
              },
              {
                id: 3,
                label: "Other Services",
                value: data.otherServicesRevenue || 0,
              },
            ].filter((item) => item.value > 0)}
            height={350}
            showLegend={true}
            valueFormatter={(value) =>
              value != null
                ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "$0.00"
            }
            onSliceClick={(slice) => handleServiceMixClick(slice, data)}
          />
        </Grid>
      </Grid>
    );
  }, [
    chartColors.semantic.primary,
    chartColors.semantic.secondary,
    handleServiceMixClick,
    theme.palette.background.alt,
    theme.palette.grey,
    theme.palette.primary.border2,
    theme.palette.primary.main,
  ]);

  return (
    <>
      <DashboardReport
        title="Revenue Summary"
        description="Comprehensive revenue analysis with Year-over-Year and Month-over-Month comparisons, customer lifetime value, retention metrics, moving averages, seasonality patterns, top performers, and concentration analysis."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
        useBarChart={drillDown.useBarChart}
      />
    </>
  );
};

/**
 * Revenue by Client Report - Comprehensive client performance analysis
 */
export const RevenueByClient = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = () => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  };

  const dataFetchFn = useCallback(async (filters) => {
    const response = await api.get("/reports/financial/revenue-by-client", {
      params: filters,
    });
    return response.data;
  }, []);

  const handleTierClick = useCallback((tier, data) => {
    let tierData = [];
    let tierTitle = "";
    let tierSubtitle = "";
    let tierDescription = null;

    if (tier === "top") {
      tierData = (data.tableData || []).slice(0, data.topTier?.count || 0);
      tierTitle = "Top Tier Clients (Top 20%)";
      tierSubtitle = `${tierData.length} highest-revenue clients generating ${data.topTier?.percentage || 0}% of total revenue`;
      tierDescription = (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            For the current time range
          </Typography>
          <Typography variant="body2" paragraph>
            These are your most valuable clients and strategic accounts. They
            represent the{" "}
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              top 20%
            </Box>{" "}
            by revenue and should receive:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <li>Priority account management and dedicated support</li>
            <li>Regular business reviews and relationship building</li>
            <li>First access to new services and capabilities</li>
            <li>Custom pricing and service level agreements</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1.5, fontStyle: "italic" }}>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "warning.main" }}
            >
              Focus:
            </Box>{" "}
            Retention, expansion, and reference development. Losing even one of
            these clients would significantly impact revenue.
          </Typography>
        </Box>
      );
    } else if (tier === "mid") {
      const startIdx = data.topTier?.count || 0;
      tierData = (data.tableData || []).slice(
        startIdx,
        startIdx + (data.midTier?.count || 0),
      );
      tierTitle = "Mid Tier Clients (Middle 30%)";
      tierSubtitle = `${tierData.length} mid-range clients generating ${data.midTier?.percentage || 0}% of total revenue`;
      tierDescription = (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            For the current time range
          </Typography>
          <Typography variant="body2" paragraph>
            These clients have significant growth potential and represent your
            emerging accounts. They are currently in the{" "}
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              middle 30%
            </Box>{" "}
            by revenue but could become top-tier with the right attention.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <li>Strong candidates for upselling and cross-selling</li>
            <li>Regular engagement to understand expanding needs</li>
            <li>Opportunity to increase service utilization</li>
            <li>Potential to grow into strategic accounts</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1.5, fontStyle: "italic" }}>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "success.main" }}
            >
              Focus:
            </Box>{" "}
            Growth and expansion. Many of tomorrow's top clients are in this
            segment today.
          </Typography>
        </Box>
      );
    } else {
      const startIdx = (data.topTier?.count || 0) + (data.midTier?.count || 0);
      tierData = (data.tableData || []).slice(startIdx);
      tierTitle = "Bottom Tier Clients (Bottom 50%)";
      tierSubtitle = `${tierData.length} lower-revenue clients generating ${data.bottomTier?.percentage || 0}% of total revenue`;
      tierDescription = (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            For the current time range
          </Typography>
          <Typography variant="body2" paragraph>
            While these clients represent the{" "}
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              bottom 50%
            </Box>{" "}
            by revenue, they still contribute to your business. However, it's
            important to ensure they are profitable.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <li>Evaluate profitability at current service/pricing levels</li>
            <li>Consider efficiency improvements or pricing adjustments</li>
            <li>
              Identify clients ready to grow vs. those maintaining steady state
            </li>
            <li>
              May benefit from self-service options or streamlined processes
            </li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1.5, fontStyle: "italic" }}>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "warning.main" }}
            >
              Focus:
            </Box>{" "}
            Profitability optimization. Ensure each account is worth the
            resources invested, or adjust service delivery model accordingly.
          </Typography>
        </Box>
      );
    }

    setDrillDown({
      open: true,
      title: tierTitle,
      subtitle: tierSubtitle,
      description: tierDescription,
      data: tierData,
      columns: [
        { field: "clientFullName", headerName: "Client Name", width: 250 },
        { field: "clientType", headerName: "Type", width: 120 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 130,
          renderCell: (row) =>
            `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        { field: "servicesCount", headerName: "Services", width: 90 },
        {
          field: "profitMargin",
          headerName: "Margin %",
          width: 100,
          renderCell: (row) => `${row.profitMargin?.toFixed(2)}%`,
        },
        {
          field: "yoyGrowth",
          headerName: "YoY Growth",
          width: 120,
          renderCell: (row) => formatPercentValue(row.yoyGrowth, { digits: 2 }),
        },
      ],
      summary: {
        metrics: [
          { label: "Clients", value: tierData.length },
          {
            label: "Total Revenue",
            value: `$${tierData.reduce((sum, c) => sum + c.revenue, 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color:
              tier === "top"
                ? "success.main"
                : tier === "mid"
                  ? "info.main"
                  : "warning.main",
          },
        ],
      },
    });
  }, []);

  const handleNewClientRevenueClick = useCallback(
    async (data) => {
      try {
        const response = await api.get(
          "/reports/financial/client-retention-details",
          { params: filters },
        );
        const retentionData = response.data.data;

        setDrillDown({
          open: true,
          title: "New Client Revenue Analysis",
          subtitle: `${retentionData.newClients?.length || 0} new clients • $${data.newClientRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          description: (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
              >
                New Client Acquisition Details
              </Typography>
              <Typography variant="body2" paragraph>
                These clients had NO services in the previous period but started
                doing business with you during the current period. New client
                revenue indicates sales and marketing effectiveness.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Health Check:</strong> New clients should represent
                20-30% of total revenue for healthy growth. Too high (&gt;40%)
                may indicate retention problems; too low (&lt;15%) suggests
                stagnant market penetration.
              </Typography>
            </Box>
          ),
          data:
            retentionData.newClients?.map((c) => ({ ...c, status: "New" })) ||
            [],
          columns: [
            { field: "name", headerName: "Client Name", width: 250 },
            {
              field: "revenue",
              headerName: "Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
            {
              field: "lastServiceDate",
              headerName: "Last Service Date",
              width: 140,
              renderCell: (row) =>
                row.lastServiceDate
                  ? new Date(row.lastServiceDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "-",
            },
          ],
          summary: {
            metrics: [
              {
                label: "New Clients",
                value: retentionData.newClients?.length || 0,
                color: "info.main",
              },
              {
                label: "Total Revenue",
                value: `$${data.newClientRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                color: "success.main",
              },
              {
                label: "Avg per Client",
                value: `$${((data.newClientRevenue || 0) / (retentionData.newClients?.length || 1))?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "% of Total Revenue",
                value: sharePercent(data.newClientRevenue, data.totalRevenue, 1),
              },
            ],
          },
        });
      } catch (error) {
        console.error("Error fetching new client details:", error);
      }
    },
    [filters],
  );

  const handleRetainedClientRevenueClick = useCallback(
    async (data) => {
      try {
        const response = await api.get(
          "/reports/financial/client-retention-details",
          { params: filters },
        );
        const retentionData = response.data.data;

        setDrillDown({
          open: true,
          title: "Retained Client Revenue Analysis",
          subtitle: `${retentionData.retainedClients?.length || 0} retained clients • $${data.retainedClientRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          description: (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "success.main" }}
              >
                Retained Client Base Details
              </Typography>
              <Typography variant="body2" paragraph>
                These clients had services in BOTH the current period and the
                previous period - they are your loyal, ongoing client base.
                Retained client revenue indicates business stability and client
                satisfaction.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Health Check:</strong> 70-80% of revenue should come
                from retained clients for a healthy, stable business. These
                clients are 5-25x cheaper to serve than acquiring new ones and
                provide predictable recurring revenue.
              </Typography>
            </Box>
          ),
          data:
            retentionData.retainedClients?.map((c) => ({
              ...c,
              status: "Retained",
            })) || [],
          columns: [
            { field: "name", headerName: "Client Name", width: 250 },
            {
              field: "revenue",
              headerName: "Current Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
              field: "previousRevenue",
              headerName: "Previous Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.previousRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
              field: "revenueChange",
              headerName: "Change",
              width: 120,
              renderCell: (row) => {
                if (row.revenueChange == null) return "-";
                const changeSymbol = row.revenueChange > 0 ? "+" : "";
                return `${changeSymbol}$${row.revenueChange?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              },
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
          ],
          summary: {
            metrics: [
              {
                label: "Retained Clients",
                value: retentionData.retainedClients?.length || 0,
                color: "success.main",
              },
              {
                label: "Total Revenue",
                value: `$${data.retainedClientRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                color: "success.main",
              },
              {
                label: "Avg per Client",
                value: `$${((data.retainedClientRevenue || 0) / (retentionData.retainedClients?.length || 1))?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "% of Total Revenue",
                value: sharePercent(data.retainedClientRevenue, data.totalRevenue, 1),
              },
            ],
          },
        });
      } catch (error) {
        console.error("Error fetching retained client details:", error);
      }
    },
    [filters],
  );

  const handleClientRetentionClick = useCallback(
    async (data) => {
      try {
        const response = await api.get(
          "/reports/financial/client-retention-details",
          { params: filters },
        );
        const retentionData = response.data.data;

        setDrillDown({
          open: true,
          title: "Client Retention Analysis",
          subtitle: `${retentionData.summary.retentionRate}% retention • ${retentionData.summary.churned} clients lost`,
          description: (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
              >
                Complete Client Retention Breakdown
              </Typography>
              <Typography variant="body2" paragraph>
                This analysis compares clients between the{" "}
                <strong>current period</strong> and the{" "}
                <strong>previous period</strong> of equal length.
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                • <strong>Retained clients:</strong> Had services in BOTH
                periods (continued doing business)
                <br />• <strong>Churned clients:</strong> Had services in
                previous period but NONE in current period (stopped using your
                services)
                <br />• <strong>New clients:</strong> Started in the current
                period only (no previous history)
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: "background.alt",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" display="block">
                  <strong>Retention Rate:</strong>{" "}
                  {retentionData.summary.retentionRate}%
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Churn Rate:</strong> {retentionData.summary.churnRate}
                  %
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Retained:</strong> {retentionData.summary.retained}{" "}
                  clients
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Churned:</strong> {retentionData.summary.churned}{" "}
                  clients
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>New:</strong> {retentionData.summary.new} clients
                </Typography>
              </Box>
            </Box>
          ),
          data: [
            ...retentionData.retainedClients.map((c) => ({
              ...c,
              status: "Retained",
            })),
            ...retentionData.churnedClients.map((c) => ({
              ...c,
              status: "Churned",
            })),
            ...retentionData.newClients.map((c) => ({ ...c, status: "New" })),
          ],
          columns: [
            { field: "name", headerName: "Client Name", width: 250 },
            {
              field: "status",
              headerName: "Status",
              width: 120,
              renderCell: (row) => (
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      row.status === "Retained"
                        ? "success.main"
                        : row.status === "Churned"
                          ? "error.main"
                          : "info.main",
                    fontWeight: 600,
                  }}
                >
                  {row.status}
                </Typography>
              ),
            },
            {
              field: "revenue",
              headerName: "Current Revenue",
              width: 150,
              renderCell: (row) =>
                row.revenue
                  ? `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "-",
            },
            {
              field: "previousRevenue",
              headerName: "Previous Revenue",
              width: 150,
              renderCell: (row) =>
                row.previousRevenue
                  ? `$${row.previousRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "-",
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
          ],
          summary: {
            metrics: [
              {
                label: "Retention Rate",
                value: `${retentionData.summary.retentionRate}%`,
                color:
                  retentionData.summary.retentionRate >= 90
                    ? "success.main"
                    : "warning.main",
              },
              {
                label: "Churn Rate",
                value: `${retentionData.summary.churnRate}%`,
                color:
                  retentionData.summary.churnRate <= 10
                    ? "success.main"
                    : "error.main",
              },
              {
                label: "Retained Clients",
                value: retentionData.summary.retained,
                color: "success.main",
              },
              {
                label: "Churned Clients",
                value: retentionData.summary.churned,
                color: "error.main",
              },
              {
                label: "New Clients",
                value: retentionData.summary.new,
                color: "info.main",
              },
            ],
          },
        });
      } catch (error) {
        console.error("Error fetching client retention details:", error);
      }
    },
    [filters],
  );

  const handleClientStatusClick = useCallback(
    async (statusType, data) => {
      try {
        const response = await api.get(
          "/reports/financial/client-retention-details",
          {
            params: filters,
          },
        );
        const retentionData = response.data.data;

        let clients = [];
        let title = "";
        let subtitle = "";
        let description = "";

        if (statusType === "new") {
          clients = retentionData.newClients || [];
          title = "New Client Revenue Details";
          subtitle = `${clients.length} new clients • $${data.newClientRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          description = (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
              >
                New Client Acquisition Analysis
              </Typography>
              <Typography variant="body2" paragraph>
                These clients had NO revenue in the previous period but started
                doing business with you during the current period. New client
                revenue indicates sales and marketing effectiveness.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Health Check:</strong> New clients should represent
                20-30% of total revenue for healthy growth. Too high (&gt;40%)
                may indicate retention problems; too low (&lt;15%) suggests
                stagnant market penetration.
              </Typography>
            </Box>
          );
        } else {
          clients = retentionData.retainedClients || [];
          title = "Retained Client Revenue Details";
          subtitle = `${clients.length} retained clients • $${data.retainedClientRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          description = (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "success.main" }}
              >
                Retained Client Base Analysis
              </Typography>
              <Typography variant="body2" paragraph>
                These clients had revenue in BOTH the current period AND the
                previous period - they are your loyal, recurring clients.
                Retained revenue is the foundation of business stability and
                profitability.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Why This Matters:</strong> Retained clients are 5-25x
                cheaper to serve than acquiring new ones. They typically spend
                more over time and provide predictable revenue. Target 70-80% of
                revenue from retained clients.
              </Typography>
            </Box>
          );
        }

        setDrillDown({
          open: true,
          title,
          subtitle,
          description,
          data: clients.map((c) => ({
            ...c,
            status: statusType === "new" ? "New" : "Retained",
          })),
          columns: [
            { field: "name", headerName: "Client Name", width: 250 },
            {
              field: "revenue",
              headerName: "Current Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            ...(statusType === "retained"
              ? [
                  {
                    field: "previousRevenue",
                    headerName: "Previous Revenue",
                    width: 150,
                    renderCell: (row) =>
                      `$${row.previousRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  },
                  {
                    field: "change",
                    headerName: "Change",
                    width: 120,
                    renderCell: (row) => {
                      const change = row.revenue - row.previousRevenue;
                      const changePercent = (
                        (change / row.previousRevenue) *
                        100
                      ).toFixed(1);
                      return `${change >= 0 ? "+" : ""}${changePercent}%`;
                    },
                  },
                ]
              : []),
            { field: "servicesCount", headerName: "Services", width: 100 },
            {
              field: "lastServiceDate",
              headerName: "Last Service Date",
              width: 140,
              renderCell: (row) =>
                row.lastServiceDate
                  ? new Date(row.lastServiceDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    
                    })
                  : "-",
            },
          ],
          summary: {
            metrics: [
              {
                label: "Total Clients",
                value: clients.length,
                color: statusType === "new" ? "info.main" : "success.main",
              },
              {
                label: "Total Revenue",
                value: `$${(statusType === "new" ? data.newClientRevenue : data.retainedClientRevenue)?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                color: "success.main",
              },
              {
                label: "Avg per Client",
                value: `$${((statusType === "new" ? data.newClientRevenue : data.retainedClientRevenue) / clients.length)?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "% of Total",
                value: sharePercent(statusType === "new" ? data.newClientRevenue : data.retainedClientRevenue, data.totalRevenue, 1),
              },
            ],
          },
        });
      } catch (error) {
        console.error("Error fetching client status details:", error);
      }
    },
    [filters],
  );

  const handleClientTypeClick = useCallback(
    async (clientType, data) => {
      try {
        const response = await api.get("/reports/financial/revenue-by-client", {
          params: filters,
        });
        const clientData = response.data.data;

        // Filter clients by type
        const typeClients = (clientData.tableData || []).filter(
          (c) => c.clientType === clientType.type,
        );

        setDrillDown({
          open: true,
          title: `${clientType.type} Clients Revenue Details`,
          subtitle: `${typeClients.length} clients • $${clientType.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          description: (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "primary.main" }}
              >
                {clientType.type} Client Portfolio Analysis
              </Typography>
              <Typography variant="body2" paragraph>
                This shows all clients in the <strong>{clientType.type}</strong>{" "}
                category and their revenue contribution. Understanding client
                type distribution helps you identify which market segments drive
                your business.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Key Insights:</strong> Review the revenue concentration,
                growth trends, and profitability within this client type.
                Consider whether you're over-dependent on one segment or if
                there are opportunities to expand in this category.
              </Typography>
            </Box>
          ),
          data: typeClients,
          columns: [
            { field: "clientFullName", headerName: "Client Name", width: 250 },
            {
              field: "revenue",
              headerName: "Revenue",
              width: 130,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            { field: "servicesCount", headerName: "Services", width: 90 },
            {
              field: "profitMargin",
              headerName: "Margin %",
              width: 100,
              renderCell: (row) => `${row.profitMargin?.toFixed(2)}%`,
            },
            {
              field: "yoyGrowth",
              headerName: "YoY Growth",
              width: 120,
              renderCell: (row) => formatPercentValue(row.yoyGrowth, { digits: 2 }),
            },
            {
              field: "isNewClient",
              headerName: "Status",
              width: 100,
              renderCell: (row) => (row.isNewClient ? "New" : "Retained"),
            },
          ],
          summary: {
            metrics: [
              {
                label: "Total Clients",
                value: typeClients.length,
                color: "primary.main",
              },
              {
                label: "Total Revenue",
                value: `$${clientType.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                color: "success.main",
              },
              {
                label: "Avg per Client",
                value: `$${(clientType.revenue / typeClients.length)?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "% of Total Revenue",
                value: sharePercent(clientType.revenue, data.totalRevenue, 1),
              },
            ],
          },
        });
      } catch (error) {
        console.error("Error fetching client type details:", error);
      }
    },
    [filters],
  );

  const metricsConfig = useCallback(
    (data) => {
      // Generate dynamic YoY insight
      const yoyInsight =
        data.yoyGrowth != null
          ? data.yoyGrowth > 0
            ? `\n\n📈 Client revenue is ${Math.abs(data.yoyGrowth).toFixed(2)}% HIGHER than the same period last year, indicating growth.`
            : data.yoyGrowth < 0
              ? `\n\n📉 Client revenue is ${Math.abs(data.yoyGrowth).toFixed(2)}% LOWER than the same period last year, indicating a decline.`
              : `\n\n➡️ Client revenue is unchanged compared to the same period last year.`
          : "";

      return [
        {
          groupTitle: "Client Portfolio Overview",
          metrics: [
            {
              title: "Total Clients",
              value: data.totalClients?.toLocaleString() || "0",
              subtitle: `${data.newClientsCount || 0} new, ${data.retainedClientsCount || 0} retained`,
              tooltipTitle: "Client Count & Acquisition",
              tooltipContent:
                "Total number of unique clients with revenue in the selected period. New clients are those without revenue in the same period last year. Retained clients had revenue in both periods.",
            },
            {
              title: "Total Revenue",
              value: `$${data.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              trend: data.yoyGrowth,
              trendLabel: "YoY growth",
              tooltipTitle: "Total Client Revenue & YoY Growth",
              tooltipContent: `Gross revenue from all clients (AccountCharge) in the selected period. YoY (Year-over-Year) growth compares this period's revenue to the same period last year.${yoyInsight}`,
            },
            {
              title: "Avg Revenue per Client",
              value: `$${data.avgRevenuePerClient?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: "Per client",
              tooltipTitle: "Average Revenue per Client (ARC)",
              tooltipContent:
                "Avg Revenue per Client = Total Revenue ÷ Number of Clients\n\nThis shows how much the typical client spends with you during the selected time period.\n\nHow to use this metric:\n• Identify your ideal client profile - who spends more than average?\n• Track over time - is it increasing (clients spending more) or decreasing (spending less)?\n• Segment analysis - compare ARC across industries, client types, or regions\n• Set targets - aim to move more clients toward higher spend levels\n\nExample: If ARC is $10,000/year but your top 20% of clients average $40,000/year, focus on understanding what makes those high-value relationships work, then replicate that success with other clients.\n\nWhy it matters: Increasing ARC is often more profitable than acquiring new clients. A client spending $20K/year is typically more profitable than four clients spending $5K each, due to relationship management efficiency.",
            },
            {
              title: "Client Profitability",
              value: `${data.avgProfitMargin?.toFixed(2)}%`,
              subtitle: "Avg margin",
              color:
                data.avgProfitMargin >= 40
                  ? "success.main"
                  : data.avgProfitMargin >= 25
                    ? "warning.main"
                    : "error.main",
              tooltipTitle: "Average Client Profit Margin",
              tooltipContent:
                "Average profit margin across all clients. Healthy margins are typically 30-50% for service businesses. Low margins may indicate pricing issues or high delivery costs.",
            },
          ],
        },
        {
          groupTitle: "Client Acquisition & Retention",
          metrics: [
            {
              title: "New Client Revenue",
              value: `$${data.newClientRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: `${data.newClientsCount || 0} new clients`,
              color: "info.main",
              tooltipTitle: "Revenue from New Clients (New Client Acquisition)",
              tooltipContent:
                "Revenue from clients who had NO revenue in the same period last year - these are newly acquired clients.\n\nNew client revenue indicates:\n• Sales & marketing effectiveness\n• Market expansion success\n• Business development team performance\n• Market demand for your services\n\nHealthy business mix: Typically 20-30% of revenue from new clients, 70-80% from retained clients.\n• Too much new revenue (>40%) may indicate retention problems\n• Too little new revenue (<15%) may indicate stagnant growth or market saturation\n\nWhy it matters: New clients are essential for growth, but they're also more expensive to acquire than retaining existing ones. Track your customer acquisition cost (CAC) - how much you spend in sales/marketing to land each new client. Compare CAC to the revenue from new clients to ensure your acquisition efforts are profitable.\n\nGoal: Maintain steady new client acquisition while maximizing retention and expansion of existing clients.",
              onClick: () => handleNewClientRevenueClick(data),
            },
            {
              title: "Retained Client Revenue",
              value: `$${data.retainedClientRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: `${data.retainedClientsCount || 0} retained clients`,
              color: "success.main",
              tooltipTitle:
                "Revenue from Retained Clients (Client Base Revenue)",
              tooltipContent:
                "Revenue from clients who had revenue in BOTH this period AND the same period last year - these are your retained, ongoing clients.\n\nRetained client revenue indicates:\n• Client satisfaction and loyalty\n• Service quality and reliability\n• Relationship strength\n• Business stability and predictability\n\nWhy this matters MORE than new client revenue:\n• Retained clients are 5-25x cheaper to serve than acquiring new ones\n• They typically spend more over time as the relationship deepens\n• They're more likely to refer new clients\n• They provide predictable, recurring revenue\n\nHealthy benchmark: 70-80% of total revenue should come from retained clients.\n\nRed flags:\n• Declining retained revenue = clients are leaving or spending less\n• Flat retained revenue = no client expansion or upselling\n\nGreen flags:\n• Growing retained revenue = successful account management and service expansion\n\nFocus on both retention AND expansion within existing accounts for sustainable, profitable growth.",
              onClick: () => handleRetainedClientRevenueClick(data),
            },
            {
              title: "Client Retention Rate",
              value: `${data.retainedClientsCount && data.totalClients ? ((data.retainedClientsCount / data.totalClients) * 100).toFixed(2) : "0.00"}%`,
              subtitle: "Of total client base",
              color:
                (data.retainedClientsCount / data.totalClients) * 100 >= 80
                  ? "success.main"
                  : "warning.main",
              tooltipTitle: "Client Retention Rate",
              tooltipContent:
                "Percentage of clients who continue doing business with you. High retention (>80%) indicates strong client satisfaction. Low retention suggests service or relationship issues.",
              onClick: () => handleClientRetentionClick(data),
            },
          ],
        },
        {
          groupTitle: "Revenue Concentration & Risk",
          metrics: [
            {
              title: "Revenue Concentration",
              value: `${data.concentrationRatio?.toFixed(2) || "0.00"}%`,
              subtitle: `From top ${data.top20Count || 0} clients (20%)`,
              color:
                data.concentrationRatio >= 80
                  ? "warning.main"
                  : data.concentrationRatio >= 60
                    ? "info.main"
                    : "success.main",
              tooltipTitle: "Revenue Concentration Risk",
              tooltipContent:
                "Percentage of revenue from your top 20% of clients. Very high concentration (>80%) creates risk if you lose a key client. Balanced diversification (50-70%) is healthier for long-term stability.",
            },
            {
              title: "Top Tier Clients",
              value: data.topTier?.count || 0,
              subtitle: `$${data.topTier?.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"} (${data.topTier?.percentage || 0}%)`,
              color: "success.main",
              tooltipTitle: "Top 20% Clients",
              tooltipContent:
                "Your highest-revenue clients (top 20%). These are your most valuable accounts and should receive priority attention and relationship management. Click to view detailed list.",
              onClick: () => handleTierClick("top", data),
            },
            {
              title: "Mid Tier Clients",
              value: data.midTier?.count || 0,
              subtitle: `$${data.midTier?.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"} (${data.midTier?.percentage || 0}%)`,
              color: "info.main",
              tooltipTitle: "Middle 30% Clients",
              tooltipContent:
                "Mid-tier clients with growth potential. These accounts may be good candidates for upselling or expanding service offerings. Click to view detailed list.",
              onClick: () => handleTierClick("mid", data),
            },
            {
              title: "Bottom Tier Clients",
              value: data.bottomTier?.count || 0,
              subtitle: `$${data.bottomTier?.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"} (${data.bottomTier?.percentage || 0}%)`,
              color: "warning.main",
              tooltipTitle: "Bottom 50% Clients",
              tooltipContent:
                "Lower-revenue clients (bottom 50%). Evaluate if these accounts are profitable at current service levels or if they need different pricing/service models. Click to view detailed list.",
              onClick: () => handleTierClick("bottom", data),
            },
          ],
        },
      ];
    },
    [
      handleTierClick,
      handleNewClientRevenueClick,
      handleRetainedClientRevenueClick,
      handleClientRetentionClick,
    ],
  );

  const chartsConfig = useCallback(
    (data) => {
      // Drill-down handlers
      const handleTopClientsClick = () => {
        setDrillDown({
          open: true,
          title: "All Clients by Revenue",
          subtitle: "Complete client portfolio breakdown",
          description:
            "This is your complete client roster ranked by revenue contribution. Use this comprehensive view to analyze your entire client base and make strategic decisions. Key insights to look for:\n\n📊 PORTFOLIO HEALTH:\n• Revenue Distribution: Is revenue concentrated in a few clients (risky) or well distributed (stable)?\n• Client Types: Which industries or client types are most valuable?\n• New vs Retained: Are new clients contributing meaningful revenue or is growth stagnant?\n\n💰 PROFITABILITY PATTERNS:\n• Margin %: Which clients are most profitable? High revenue doesn't always mean high profit\n• Low margin clients may need pricing adjustments or service delivery optimization\n\n📈 GROWTH INDICATORS:\n• YoY Growth: Which clients are growing (invest more) vs declining (risk of churn)?\n• Negative YoY growth is an early warning signal - reach out to understand why\n\n🎯 ACTION ITEMS:\n• Identify top 20% clients (your VIPs) - ensure they have dedicated attention\n• Find mid-tier clients with positive YoY growth - they're your expansion opportunities\n• Review bottom-tier clients (low revenue, low margin) - are they worth the effort?\n• Spot 'New' clients who could grow into major accounts with proper nurturing\n\nUse the Status column to distinguish new client wins from established relationships. Sort by different columns to find patterns and opportunities.",
          data: data.tableData || [],
          columns: [
            { field: "clientFullName", headerName: "Client Name", width: 250 },
            { field: "clientType", headerName: "Type", width: 120 },
            {
              field: "revenue",
              headerName: "Revenue",
              width: 130,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            { field: "servicesCount", headerName: "Services", width: 90 },
            {
              field: "profitMargin",
              headerName: "Margin %",
              width: 100,
              renderCell: (row) => `${row.profitMargin?.toFixed(2)}%`,
            },
            {
              field: "yoyGrowth",
              headerName: "YoY Growth",
              width: 120,
              renderCell: (row) => formatPercentValue(row.yoyGrowth, { digits: 2 }),
            },
            {
              field: "isNewClient",
              headerName: "Status",
              width: 100,
              renderCell: (row) => (row.isNewClient ? "New" : "Retained"),
            },
          ],
          summary: {
            metrics: [
              { label: "Total Clients", value: data.totalClients },
              {
                label: "Total Revenue",
                value: `$${data.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "Avg per Client",
                value: `$${data.avgRevenuePerClient?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
            ],
          },
        });
      };

      return (
        <Grid container spacing={3}>
          {/* Top Clients Bar Chart */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <ReportBarChart
              title="Top 10 Clients by Revenue"
              tooltipTitle="Client Revenue Ranking (Your VIP Clients)"
              tooltipContent={`Your top 10 clients by revenue - these are your most valuable relationships and deserve special attention.

Why track this:
• RELATIONSHIP PRIORITY: These clients should have dedicated account managers and regular check-ins
• RISK MONITORING: Track if any single client dominates - losing them would hurt
• SUCCESS PATTERNS: What do these top clients have in common? Industry? Service type? Can you replicate?
• EXPANSION OPPORTUNITIES: Are these clients maxed out, or could they spend more with better service/offerings?

Best practices for top clients:
1. Quarterly business reviews
2. Proactive communication (don't wait for problems)
3. First access to new services
4. Dedicated support contacts
5. Regular satisfaction surveys

Click any bar to see ALL clients ranked by revenue with detailed metrics.`}
              data={
                data.tableData?.slice(0, 10).map((c) => ({
                  name: c.clientName,
                  fullName: c.clientFullName,
                  revenue: c.revenue,
                  services: c.servicesCount,
                  margin: c.profitMargin,
                })) || []
              }
              xAxisKey="name"
              tooltipKey="fullName"
              series={[
                {
                  dataKey: "revenue",
                  label: "Revenue",
                  color: chartColors.semantic.primary,
                },
              ]}
              height={400}
              valueFormatter={(value) =>
                value != null
                  ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "$0.00"
              }
              onBarClick={handleTopClientsClick}
            />
          </Grid>

          {/* Revenue Concentration Pie Chart */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <ReportPieChart
              title="Revenue Concentration by Tier"
              tooltipTitle="Client Tier Distribution (Portfolio Segmentation)"
              tooltipContent={`This shows how your total revenue is distributed across three strategic client tiers:

🟢 Top 20% - Largest clients (highest revenue each)
🔵 Middle 30% - Mid-size clients (moderate revenue)
🟠 Bottom 50% - Smaller clients (lower revenue)

Typical patterns:
• Top tier often represents 60-80% of total revenue despite being only 20% of clients
• Middle tier usually contributes 15-25% (these are your growth opportunities)
• Bottom tier often generates 5-15% (evaluate profitability vs. effort)

Strategy for each tier:
🟢 TOP: Protect and expand - retention is #1 priority
🔵 MID: Grow and nurture - can they become top-tier?
🟠 BOTTOM: Optimize efficiency - are they profitable at current service levels?

Click any segment to see the strategic recommendations and client details for that tier.`}
              data={[
                {
                  id: 1,
                  label: `Top 20% (${data.topTier?.count || 0})`,
                  value: data.topTier?.revenue || 0,
                },
                {
                  id: 2,
                  label: `Mid 30% (${data.midTier?.count || 0})`,
                  value: data.midTier?.revenue || 0,
                },
                {
                  id: 3,
                  label: `Bottom 50% (${data.bottomTier?.count || 0})`,
                  value: data.bottomTier?.revenue || 0,
                },
              ].filter((item) => item.value > 0)}
              height={400}
              showLegend={true}
              valueFormatter={(value) =>
                value != null
                  ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "$0.00"
              }
              onSliceClick={(item) => {
                if (item.id === 1) handleTierClick("top", data);
                else if (item.id === 2) handleTierClick("mid", data);
                else handleTierClick("bottom", data);
              }}
            />
          </Grid>

          {/* Client Acquisition Breakdown */}
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Revenue by Client Status"
              tooltipTitle="New vs Retained Client Revenue (Growth Health Check)"
              tooltipContent={`This breaks down your revenue into two critical sources:

🔵 New Clients: Customers with NO revenue in the same period last year (newly acquired)
🟢 Retained Clients: Customers with revenue in BOTH periods (loyal, recurring)

Healthy benchmarks:
🟢 Ideal Mix: 70-80% retained, 20-30% new
🟡 Growing Fast: 60-70% retained, 30-40% new (high growth, but watch retention)
🔴 Warning Signs:
  • <60% retained = Retention problem (clients leaving)
  • <15% new = Growth stagnation (not acquiring enough)
  • >50% new = Possible churn crisis (replacing lost clients, not growing)

Key insight: It costs 5-25x more to acquire a new client than retain an existing one. Retained revenue is more profitable and predictable.

Goal: Maximize retained revenue WHILE maintaining steady new client acquisition.`}
              data={[
                {
                  id: 1,
                  label: `New Clients (${data.newClientsCount || 0})`,
                  value: data.newClientRevenue || 0,
                },
                {
                  id: 2,
                  label: `Retained Clients (${data.retainedClientsCount || 0})`,
                  value: data.retainedClientRevenue || 0,
                },
              ].filter((item) => item.value > 0)}
              height={350}
              showLegend={true}
              valueFormatter={(value) =>
                value != null
                  ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "$0.00"
              }
              onSliceClick={(item) => {
                const statusType = item.id === 1 ? "new" : "retained";
                handleClientStatusClick(statusType, data);
              }}
            />
          </Grid>

          {/* Client Type Distribution */}
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Revenue by Client Type"
              tooltipTitle="Client Type Breakdown"
              tooltipContent="Shows revenue distribution across different client types (e.g., Corporate, Government, Healthcare, etc.). Helps identify which client segments drive your business."
              data={(data.clientTypeBreakdown || []).map((type, idx) => ({
                id: idx + 1,
                label: `${type.type} (${type.count})`,
                value: type.revenue,
              }))}
              height={350}
              showLegend={true}
              valueFormatter={(value) =>
                value != null
                  ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "$0.00"
              }
              onSliceClick={(item) => {
                const clickedType = (data.clientTypeBreakdown || [])[
                  item.id - 1
                ];
                if (clickedType) {
                  handleClientTypeClick(clickedType, data);
                }
              }}
            />
          </Grid>
        </Grid>
      );
    },
    [
      chartColors.semantic.primary,
      handleTierClick,
      handleClientStatusClick,
      handleClientTypeClick,
    ],
  );

  return (
    <>
      <DashboardReport
        title="Revenue by Client"
        description="Comprehensive client portfolio analysis with revenue breakdown, YoY growth comparisons, client acquisition metrics, retention analysis, concentration risk assessment, and client tier segmentation."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
        useBarChart={drillDown.useBarChart}
      />
    </>
  );
};

/**
 * Revenue by Service Type Report - Comprehensive service performance analysis
 */
export const RevenueByService = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const isSingleMonthSelection =
    Array.isArray(filters?.months) && filters.months.length === 1;
  const growthLabelShort = isSingleMonthSelection ? "MoM" : "YoY";
  const growthLabelLong = isSingleMonthSelection
    ? "Month-over-Month"
    : "Year-over-Year";

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = () => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  };

  const dataFetchFn = useCallback(async (filters) => {
    const response = await api.get("/reports/financial/revenue-by-service", {
      params: filters,
    });
    const backendData = response.data.data;

    const monthOrder = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const getGrowthPercent = (current, previous) => {
      const safeCurrent = Number(current || 0);
      const safePrevious = Number(previous || 0);

      if (safePrevious === 0) {
        return safeCurrent > 0 ? 100 : 0;
      }

      return ((safeCurrent - safePrevious) / safePrevious) * 100;
    };

    const roundTo2 = (value) => Number(Number(value || 0).toFixed(2));

    const isSingleMonthSelection =
      Array.isArray(filters?.months) &&
      filters.months.length === 1 &&
      Array.isArray(filters?.years) &&
      filters.years.length === 1;

    const comparisonFilters = { ...filters };

    if (isSingleMonthSelection) {
      const currentMonth = filters.months[0];
      const currentYear = parseInt(filters.years[0], 10);
      const currentMonthIndex = monthOrder.indexOf(currentMonth);

      if (currentMonthIndex >= 0 && !Number.isNaN(currentYear)) {
        const previousMonthIndex = (currentMonthIndex + 11) % 12;
        const previousYear =
          currentMonthIndex === 0 ? currentYear - 1 : currentYear;

        comparisonFilters.months = [monthOrder[previousMonthIndex]];
        comparisonFilters.years = [String(previousYear)];
      }
    } else if (Array.isArray(filters?.years) && filters.years.length > 0) {
      comparisonFilters.years = filters.years.map((year) =>
        String(parseInt(year, 10) - 1),
      );
    }

    const comparisonResponse = await api.get(
      "/reports/financial/revenue-by-service",
      {
        params: comparisonFilters,
      },
    );
    const comparisonTableData = comparisonResponse?.data?.data?.tableData || [];

    const comparisonByServiceType = comparisonTableData.reduce((acc, item) => {
      acc[item.serviceType] = item;
      return acc;
    }, {});

    // Calculate summary metrics from backend data
    const totalRevenue = backendData.tableData.reduce(
      (sum, item) => sum + item.revenue,
      0,
    );
    const totalServices = backendData.tableData.reduce(
      (sum, item) => sum + item.servicesCount,
      0,
    );
    const avgServiceValue =
      totalServices > 0 ? totalRevenue / totalServices : 0;

    // Find service type breakdown (map backend service types to our categories)
    // Aggregate into main categories
    const translationData = backendData.tableData.filter(
      (item) =>
        item.category === "Translation" ||
        item.serviceType?.toLowerCase().includes("translation"),
    );
    const interpretationData = backendData.tableData.filter(
      (item) =>
        item.category === "Interpretation" ||
        [
          "on-site",
          "onsite",
          "in-person",
          "vri",
          "video",
          "opi",
          "phone",
          "telephone",
        ].some((term) =>
          item.serviceType?.toLowerCase().includes(term.toLowerCase()),
        ),
    );
    const otherData = backendData.tableData.filter(
      (item) =>
        item.category === "Other" ||
        (!translationData.includes(item) && !interpretationData.includes(item)),
    );

    // Calculate aggregated metrics for main categories
    const calculateCategoryMetrics = (dataArray) => {
      const revenue = dataArray.reduce((sum, item) => sum + item.revenue, 0);
      const count = dataArray.reduce(
        (sum, item) => sum + item.servicesCount,
        0,
      );
      const totalProfit = dataArray.reduce(
        (sum, item) => sum + (item.profit || 0),
        0,
      );
      return {
        revenue,
        count,
        avgValue: count > 0 ? revenue / count : 0,
        margin: revenue > 0 ? (totalProfit / revenue) * 100 : 0,
        subTypes: dataArray,
      };
    };

    const translation = calculateCategoryMetrics(translationData);
    const interpretation = calculateCategoryMetrics(interpretationData);
    const otherServices = calculateCategoryMetrics(otherData);

    // Find highest margin and fastest growing (YoY not available yet from backend)
    const sortedByMargin = [...backendData.tableData].sort(
      (a, b) => b.profitMargin - a.profitMargin,
    );
    const highestMarginType = sortedByMargin[0]?.serviceType || "N/A";
    const highestMargin = sortedByMargin[0]?.profitMargin || 0;

    const comparisonTotalRevenue = comparisonTableData.reduce(
      (sum, item) => sum + Number(item.revenue || 0),
      0,
    );
    const comparisonTotalServices = comparisonTableData.reduce(
      (sum, item) => sum + Number(item.servicesCount || 0),
      0,
    );
    const comparisonAvgServiceValue =
      comparisonTotalServices > 0
        ? comparisonTotalRevenue / comparisonTotalServices
        : 0;

    const comparisonTranslationRevenue = comparisonTableData
      .filter((item) => item.category === "Translation")
      .reduce((sum, item) => sum + Number(item.revenue || 0), 0);

    const comparisonInterpretationRevenue = comparisonTableData
      .filter((item) => item.category === "Interpretation")
      .reduce((sum, item) => sum + Number(item.revenue || 0), 0);

    const comparisonOtherRevenue = comparisonTableData
      .filter((item) => item.category === "Other")
      .reduce((sum, item) => sum + Number(item.revenue || 0), 0);

    // Calculate concentration
    const topServiceTypePercentage =
      sortedByMargin[0] && totalRevenue > 0
        ? (sortedByMargin[0].revenue / totalRevenue) * 100
        : 0;

    // Enhance tableData with calculated fields
    const enhancedTableData = backendData.tableData.map((item) => ({
      ...item,
      count: item.servicesCount,
      avgValue: item.avgRevenuePerService,
      percentOfTotal:
        totalRevenue > 0 ? roundTo2((item.revenue / totalRevenue) * 100) : 0,
      yoyGrowth: roundTo2(
        getGrowthPercent(
          item.revenue,
          comparisonByServiceType[item.serviceType]?.revenue || 0,
        ),
      ),
      margin: item.profitMargin,
    }));

    const fastestGrowing = [...enhancedTableData].sort(
      (a, b) => b.yoyGrowth - a.yoyGrowth,
    )[0];

    // Distinct service types in the result set (real diversity, not hardcoded)
    const serviceTypeCount = new Set(
      backendData.tableData.map((item) => item.serviceType),
    ).size;

    return {
      data: {
        // Summary metrics
        totalRevenue,
        totalServices,
        avgServiceValue,
        yoyGrowth: roundTo2(
          getGrowthPercent(totalRevenue, comparisonTotalRevenue),
        ),
        totalServicesGrowth: roundTo2(
          getGrowthPercent(totalServices, comparisonTotalServices),
        ),
        avgServiceValueGrowth: roundTo2(
          getGrowthPercent(avgServiceValue, comparisonAvgServiceValue),
        ),
        serviceTypeCount,

        // Main category breakdown
        translationRevenue: translation.revenue,
        translationCount: translation.count,
        translationAvgValue: translation.avgValue,
        translationMargin: translation.margin,
        translationSubTypes: translation.subTypes,
        translationYoyGrowth: roundTo2(
          getGrowthPercent(translation.revenue, comparisonTranslationRevenue),
        ),

        interpretationRevenue: interpretation.revenue,
        interpretationCount: interpretation.count,
        interpretationAvgValue: interpretation.avgValue,
        interpretationMargin: interpretation.margin,
        interpretationSubTypes: interpretation.subTypes,
        interpretationYoyGrowth: roundTo2(
          getGrowthPercent(
            interpretation.revenue,
            comparisonInterpretationRevenue,
          ),
        ),

        otherServicesRevenue: otherServices.revenue,
        otherServicesCount: otherServices.count,
        otherServicesAvgValue: otherServices.avgValue,
        otherServicesMargin: otherServices.margin,
        otherServicesSubTypes: otherServices.subTypes,
        otherServicesYoyGrowth: roundTo2(
          getGrowthPercent(otherServices.revenue, comparisonOtherRevenue),
        ),

        // Most profitable service type
        highestMarginType,
        highestMargin: roundTo2(highestMargin),

        // Fastest growing service type
        fastestGrowingType: fastestGrowing?.serviceType || "N/A",
        fastestGrowthRate: roundTo2(fastestGrowing?.yoyGrowth || 0),

        // Concentration
        topServiceTypePercentage: roundTo2(topServiceTypePercentage),

        // Monthly trend data (from backend monthlyTrend)
        monthlyTrend: {
          months: backendData.monthlyTrend?.months || [],
          translation: backendData.monthlyTrend?.translationRevenue || [],
          interpretation:
            backendData.monthlyTrend?.interpretationRevenue || [],
          otherServices: backendData.monthlyTrend?.otherServicesRevenue || [],
        },

        // Enhanced table data
        tableData: enhancedTableData,
      },
    };
  }, []);

  const handleServiceTypeClick = useCallback((categoryName, data) => {
    let categoryData = null;
    let subTypes = [];

    if (categoryName === "Translation") {
      categoryData = {
        revenue: data.translationRevenue,
        count: data.translationCount,
        avgValue: data.translationAvgValue,
        margin: data.translationMargin,
        yoyGrowth: data.translationYoyGrowth,
        percentOfTotal:
          data.totalRevenue > 0
            ? (data.translationRevenue / data.totalRevenue) * 100
            : 0,
      };
      subTypes = data.translationSubTypes || [];
    } else if (categoryName === "Interpretation") {
      categoryData = {
        revenue: data.interpretationRevenue,
        count: data.interpretationCount,
        avgValue: data.interpretationAvgValue,
        margin: data.interpretationMargin,
        yoyGrowth: data.interpretationYoyGrowth,
        percentOfTotal:
          data.totalRevenue > 0
            ? (data.interpretationRevenue / data.totalRevenue) * 100
            : 0,
      };
      subTypes = data.interpretationSubTypes || [];
    } else if (categoryName === "Other Services") {
      categoryData = {
        revenue: data.otherServicesRevenue,
        count: data.otherServicesCount,
        avgValue: data.otherServicesAvgValue,
        margin: data.otherServicesMargin,
        yoyGrowth: data.otherServicesYoyGrowth,
        percentOfTotal:
          data.totalRevenue > 0
            ? (data.otherServicesRevenue / data.totalRevenue) * 100
            : 0,
      };
      subTypes = data.otherServicesSubTypes || [];
    }

    setDrillDown({
      open: true,
      title: `${categoryName} - Detailed Breakdown`,
      subtitle: `${categoryData.count.toLocaleString()} services • $${categoryData.revenue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} revenue`,
      description: (
        <Box
          component="div"
          sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
          >
            {categoryName} Category Performance
          </Typography>
          <Typography variant="body2" paragraph>
            This category represents{" "}
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              {categoryData.percentOfTotal.toFixed(1)}%
            </Box>{" "}
            of your total revenue with{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {categoryData.count.toLocaleString()}
            </Box>{" "}
            services delivered.
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Key Metrics:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <li>
              Average Service Value: ${categoryData.avgValue.toFixed(2)} per
              service
            </li>
            <li>Profit Margin: {categoryData.margin.toFixed(2)}%</li>
            <li>
              Year-over-Year Growth:{" "}
              {formatPercentValue(categoryData.yoyGrowth, { digits: 2, signed: true })}
            </li>
          </Box>

          {subTypes.length > 0 && (
            <>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mt: 2, mb: 1 }}
              >
                Service Type Breakdown:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontStyle: "italic", color: "text.secondary" }}
              >
                The table below shows all {subTypes.length} service type
                {subTypes.length !== 1 ? "s" : ""} within this category, ranked
                by revenue contribution.
              </Typography>
            </>
          )}
        </Box>
      ),
      data: subTypes,
      columns: [
        { field: "serviceType", headerName: "Service Type", width: 200 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 140,
          renderCell: (row) =>
            `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "servicesCount",
          headerName: "Count",
          width: 100,
        },
        {
          field: "avgRevenuePerService",
          headerName: "Avg Value",
          width: 130,
          renderCell: (row) =>
            `$${row.avgRevenuePerService?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        {
          field: "profitMargin",
          headerName: "Margin %",
          width: 100,
          renderCell: (row) => `${row.profitMargin?.toFixed(2)}%`,
        },
      ],
      summary: {
        metrics: [
          { label: "Total Service Types", value: subTypes.length },
          {
            label: "Total Revenue",
            value: `$${categoryData.revenue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            color: "success.main",
          },
          {
            label: "Avg per Service",
            value: `$${categoryData.avgValue.toFixed(2)}`,
          },
          {
            label: "Profit Margin",
            value: `${categoryData.margin.toFixed(2)}%`,
            color:
              categoryData.margin > 35
                ? "success.main"
                : categoryData.margin > 25
                  ? "info.main"
                  : "warning.main",
          },
        ],
      },
    });
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      return [
        {
          groupTitle: "Overall Service Performance",
          metrics: [
            {
              title: "Total Revenue",
              value: `$${data.totalRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              trend: data.yoyGrowth,
              trendLabel: `${growthLabelShort} growth`,
              tooltipTitle: `Total Service Revenue & ${growthLabelShort} Growth`,
              tooltipContent: `Total revenue from all service types (On-Site, Translation, VRI, OPI, etc.) during the selected period.\n\n${growthLabelLong} Growth: Compares this period's revenue to the ${isSingleMonthSelection ? "previous month" : "same period last year"}.\n• ${formatPercentValue(data.yoyGrowth, { digits: 2, signed: true })} means you generated ${Math.abs(data.yoyGrowth)}% ${data.yoyGrowth > 0 ? "MORE" : "LESS"} revenue than ${isSingleMonthSelection ? "last month" : "last year"}\n\nWhat drives service revenue:\n• Volume: More services delivered\n• Pricing: Higher rates per service\n• Mix shift: Moving toward higher-value service types\n• Market demand: Overall market growth\n\nHealthy benchmarks:\n🟢 10%+ ${growthLabelShort} growth = Strong performance\n🟡 5-10% growth = Steady, sustainable growth\n🔴 <5% or negative = Investigate market trends, competition, pricing`,
            },
            {
              title: "Total Services",
              value: data.totalServices?.toLocaleString() || "0",
              trend: data.totalServicesGrowth,
              trendLabel: `${growthLabelShort} growth`,
              subtitle: "Services delivered",
              tooltipTitle: "Total Service Count (Volume)",
              tooltipContent: `Total number of services delivered across all service types during the selected period.\n\nService Count = Total volume of work delivered regardless of size or revenue\n\nWhy volume matters:\n• CAPACITY PLANNING: Shows how much work your team is handling\n• EFFICIENCY TRENDS: Compare volume to revenue - are you getting more revenue per service?\n• WORKLOAD DISTRIBUTION: Helps with resource allocation and scheduling\n• GROWTH INDICATOR: Increasing volume shows market demand\n\nExample: If you delivered 1,000 services this quarter vs 850 last quarter:\n• Volume up 17.6% = Your team is busier\n• If revenue only up 10% = Average service value is declining (pricing pressure or mix shift to lower-value services)\n\nKey insight: Track both volume AND revenue. You want both growing, but revenue growing faster than volume means you're improving average service value.`,
            },
            {
              title: "Avg Service Value",
              value: `$${data.avgServiceValue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              trend: data.avgServiceValueGrowth,
              trendLabel: `${growthLabelShort} growth`,
              subtitle: "Per service",
              tooltipTitle: "Average Revenue per Service",
              tooltipContent: `Avg Service Value = Total Revenue ÷ Number of Services\n\nThis shows how much revenue you generate per service on average, combining all service types.\n\nExample: $142.54 average means:\n• Some services are very high value (On-Site at $266)\n• Some are lower (OPI at $45)\n• The mix determines your overall average\n\nWhy this metric matters:\n• PRICING POWER: Rising average = successful price increases or mix shift to premium services\n• EFFICIENCY BENCHMARK: Compare to industry standards or your own targets\n• MIX ANALYSIS: Changes indicate shift between service types\n\nWhat moves this metric:\n📈 INCREASES when:\n• You raise prices (inflation adjustments, value-based pricing)\n• More high-value services (On-Site, Translation) vs low-value (OPI, VRI)\n• Longer service durations or more complex projects\n• Premium add-ons (rush fees, after-hours, specialized linguists)\n\n📉 DECREASES when:\n• Price competition forces lower rates\n• Mix shifts to lower-value services (more OPI/VRI, less On-Site)\n• Shorter, simpler services\n• Discounting to win volume\n\nGoal: Steadily increase average service value through pricing strategy AND service mix optimization.`,
            },
            {
              title: "Service Diversity",
              value: `${data.serviceTypeCount || 0} Type${data.serviceTypeCount === 1 ? "" : "s"}`,
              subtitle: `${(data.topServiceTypePercentage || 0).toFixed(2)}% from top type`,
              color:
                data.topServiceTypePercentage > 60
                  ? "warning.main"
                  : "success.main",
              tooltipTitle: "Service Portfolio Diversification",
              tooltipContent: `Measures how diversified your service offerings are and how dependent you are on any single service type.\n\nConcentration Risk:\n• Top service type generates ${(data.topServiceTypePercentage || 0).toFixed(2)}% of revenue\n🔴 >70% = HIGH RISK - Over-dependent on one service type\n🟡 50-70% = MODERATE - Some concentration but acceptable\n🟢 <50% = WELL DIVERSIFIED - Balanced portfolio\n\nWhy diversification matters:\n• MARKET RISK: If demand for your main service type declines (technology disruption, regulatory changes, economic shifts), you're vulnerable\n• CAPACITY CONSTRAINTS: Over-reliance on one type means you can't easily pivot resources\n• GROWTH CEILING: Limited by capacity in one service area vs growing multiple types\n• CLIENT NEEDS: Diverse offerings allow cross-selling and deeper client relationships\n\nExample scenarios:\n❌ 80% from On-Site Interpretation:\n   • COVID-19 pandemic decimates on-site demand → business crisis\n   • Solution: Build VRI/OPI capabilities as alternatives\n\n✅ Balanced 40% On-Site, 30% Translation, 20% VRI, 10% OPI:\n   • If on-site drops, other services cushion the impact\n   • Can redirect linguists between service types\n   • Appeal to wider range of client needs\n\nStrategy:\n• If concentration is HIGH: Actively market underutilized service types\n• If diversified: Ensure you maintain excellence across all types (don't spread too thin)`,
            },
          ],
        },
        {
          groupTitle: "Service Type Performance",
          metrics: [
            {
              title: "Translation",
              value: `$${data.translationRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: `${data.translationCount?.toLocaleString() || 0} projects • ${sharePercent(data.translationRevenue, data.totalRevenue)} • ${formatPercentValue(data.translationMargin, { digits: 2 })} margin`,
              trend: data.translationYoyGrowth,
              trendLabel: `${growthLabelShort} growth`,
              color: "secondary.main",
              tooltipTitle: "Translation Revenue",
              tooltipContent: `Revenue from written translation projects converting documents from one language to another.\n\nCharacteristics:\n• DELIVERY: Remote, asynchronous work (documents, websites, marketing materials, legal/medical records)\n• PRICING: Project-based, often per-word or per-page (avg $${data.translationAvgValue?.toFixed(2)} per project)\n• WORKFLOW: Translator receives document → translates → quality review → delivery\n• MARGINS: Often high margins (${data.translationMargin?.toFixed(1)}%) due to scalable delivery model and specialized expertise\n\nTypes of translation projects:\n• Legal documents (contracts, patents, litigation materials)\n• Medical records (patient charts, research, consent forms)\n• Marketing content (websites, brochures, campaigns)\n• Technical documentation (manuals, specifications)\n• Financial reports, regulatory filings\n\nPricing factors:\n• Word count (most common: $0.10-$0.25/word for common languages)\n• Language pair complexity (rare languages command premium)\n• Subject matter expertise (medical, legal, technical = higher rates)\n• Turnaround time (rush projects = 50-100% premium)\n• Certification requirements (sworn/notarized translations)\n\nWhy Translation typically has highest margins:\n• SCALABLE: Translators work remotely, no travel costs\n• EFFICIENT: Can batch projects, use translation memory to accelerate work\n• SPECIALIZATION: Subject-matter expertise commands premium pricing\n• LEVERAGE: Senior translators can review junior work (pyramid model)\n\nClick to see all translation service types and their performance metrics.`,
              onClick: () => handleServiceTypeClick("Translation", data),
            },
            {
              title: "Interpretation",
              value: `$${data.interpretationRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: `${data.interpretationCount?.toLocaleString() || 0} services • ${sharePercent(data.interpretationRevenue, data.totalRevenue)} • ${formatPercentValue(data.interpretationMargin, { digits: 2 })} margin`,
              trend: data.interpretationYoyGrowth,
              trendLabel: `${growthLabelShort} growth`,
              color: "primary.main",
              tooltipTitle: "Interpretation Revenue",
              tooltipContent: `Revenue from all interpretation services - real-time spoken language conversion across On-Site, VRI, and OPI channels.\n\nCharacteristics:\n• DELIVERY: Real-time, synchronous (requires linguist available when needed)\n• PRICING: Time-based (per hour, per session, per minute depending on type)\n• AVERAGE: $${data.interpretationAvgValue?.toFixed(2)} per service across all interpretation types\n• MARGIN: ${data.interpretationMargin?.toFixed(1)}% average across all interpretation channels\n\nInterpretation Channels:\n\n1. ON-SITE INTERPRETATION:\n   • Face-to-face at client location\n   • Premium pricing (travel time, expertise)\n   • Use cases: Legal proceedings, medical procedures, high-stakes meetings\n   • Highest per-service revenue\n\n2. VRI (VIDEO REMOTE INTERPRETATION):\n   • Real-time video connection\n   • Visual cues without travel costs\n   • Use cases: Medical appointments, social services, business meetings\n   • Growing fastest (COVID-19 accelerated adoption)\n\n3. OPI (OVER-THE-PHONE INTERPRETATION):\n   • Audio-only phone connection\n   • Fastest connection, 24/7 availability\n   • Use cases: Emergency services, healthcare hotlines, quick customer service calls\n   • Highest volume, lowest per-service revenue\n\nInterpretation vs Translation:\n• Interpretation = SPOKEN, real-time, requires immediate availability\n• Translation = WRITTEN, asynchronous, can be scheduled/batched\n• Interpretation typically has more scheduling complexity\n• Translation typically has higher profit margins\n\nStrategic considerations:\n• Diversification across channels reduces risk (on-site shutdown → shift to VRI/OPI)\n• Each channel needs different infrastructure (travel logistics vs technology platforms)\n• Cross-training linguists across channels increases flexibility\n• Blended pricing strategy balances volume (OPI) with value (On-Site)\n\nClick to see detailed breakdown of all interpretation service types (On-Site, VRI, OPI, etc.).`,
              onClick: () => handleServiceTypeClick("Interpretation", data),
            },
            {
              title: "Other Services",
              value: `$${data.otherServicesRevenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: `${data.otherServicesCount?.toLocaleString() || 0} services • ${sharePercent(data.otherServicesRevenue, data.totalRevenue)} • ${formatPercentValue(data.otherServicesMargin, { digits: 2 })} margin`,
              trend: data.otherServicesYoyGrowth,
              trendLabel: `${growthLabelShort} growth`,
              color: "info.main",
              tooltipTitle: "Other Services Revenue",
              tooltipContent: `Revenue from specialized language services beyond core translation and interpretation offerings.\n\nCommon "Other Services" include:\n\n1. LOCALIZATION:\n   • Adapting content for specific markets (not just translation)\n   • Cultural adaptation, imagery, formatting, local regulations\n   • Website localization, software localization, multimedia\n   • Higher value-add than pure translation\n\n2. TRANSCRIPTION:\n   • Converting audio/video to written text\n   • Medical transcription, legal depositions, interview transcripts\n   • Often bundled with translation (transcribe + translate)\n\n3. DESKTOP PUBLISHING (DTP):\n   • Formatting translated documents to match original layout\n   • Critical for marketing materials, manuals, presentations\n   • Requires graphic design skills + language knowledge\n\n4. INTERPRETATION EQUIPMENT RENTAL:\n   • Audio equipment for conferences (headsets, transmitters, booths)\n   • Recurring revenue from events and conferences\n   • High margins on rental fees\n\n5. LANGUAGE ASSESSMENT & TESTING:\n   • Evaluating linguist qualifications\n   • Pre-employment language proficiency testing\n   • Certification and credential verification\n\n6. CONSULTING & TRAINING:\n   • Cultural competency training\n   • Language access program development\n   • Compliance consulting (Title VI, ADA, healthcare language access)\n\n7. VOICEOVER & DUBBING:\n   • Voice recording for translated videos\n   • E-learning modules, commercials, films\n   • Combines translation + voice talent\n\nWhy "Other Services" matter:\n• CROSS-SELLING: Existing clients may not know you offer these\n• HIGHER MARGINS: Often premium pricing for specialized expertise\n• DIFFERENTIATION: Competitors may only offer translation/interpretation\n• CLIENT STICKINESS: More services = harder to replace you\n• REVENUE SMOOTHING: When main services slow, these can fill gaps\n\nAverage service value: $${data.otherServicesAvgValue?.toFixed(2)}\nProfit margin: ${data.otherServicesMargin?.toFixed(1)}%\n\nStrategy:\n• If revenue is low: Opportunity to develop and market these services\n• If growing fast: Consider building dedicated teams for high-demand services\n• Package with core services (translation + DTP bundle, interpretation + equipment rental)\n\nClick to see detailed breakdown of all other service types and their performance.`,
              onClick: () => handleServiceTypeClick("Other Services", data),
            },
          ],
        },
        {
          groupTitle: "Portfolio Insights",
          metrics: [
            {
              title: "Highest Margin Type",
              value: data.highestMarginType || "N/A",
              subtitle: `${(data.highestMargin || 0).toFixed(2)}% margin`,
              color: "success.main",
              tooltipTitle: "Most Profitable Service Type",
              tooltipContent: `The service type with the highest profit margin - this is your most profitable offering per dollar of revenue.\n\nHighest Margin: ${data.highestMarginType} at ${data.highestMargin}%\n\nWhat this means:\n• ${data.highestMarginType} generates $${data.highestMargin} in profit for every $100 in revenue\n• After paying translators/interpreters and direct costs, you keep ${data.highestMargin}% as gross profit\n\nWhy Translation typically has highest margins:\n• SCALABLE: Translators work remotely, no travel costs\n• EFFICIENT: Can batch projects, use translation memory to accelerate work\n• SPECIALIZATION: Subject-matter expertise commands premium pricing\n• LEVERAGE: Senior translators can review junior work (pyramid model)\n\nStrategy implications:\n🎯 GROW HIGH-MARGIN SERVICES:\n• If Translation is 42.5% margin, prioritize winning more translation work\n• Train more translators in profitable specialties (medical, legal, technical)\n• Market translation services aggressively to existing interpretation clients\n\n⚖️ BALANCE WITH STRATEGIC GOALS:\n• Don't abandon lower-margin services if they:\n  - Attract clients who then buy high-margin services\n  - Provide competitive differentiation\n  - Generate high volume to leverage fixed costs\n\nExample: OPI might be lower margin (32.8%) but if it brings in new clients who then use Translation (42.5%), keep both.\n\nProfit impact:\n• If you shifted $10,000 revenue from OPI (32.8% margin) to Translation (42.5% margin):\n  OPI profit: $3,280\n  Translation profit: $4,250\n  Extra profit: $970 (30% more!)\n\nAction items:\n1. Identify what makes your high-margin service profitable\n2. Replicate those success factors in other services\n3. Cross-sell high-margin services to existing clients\n4. Price low-margin services appropriately (don't subsidize unprofitable work)`,
            },
            {
              title: "Fastest Growing Type",
              value: data.fastestGrowingType || "N/A",
              subtitle: `${(data.fastestGrowthRate || 0).toFixed(2)}% ${growthLabelShort} growth`,
              color: "info.main",
              tooltipTitle: "Fastest Growing Service Type",
              tooltipContent: `The service type experiencing the highest ${growthLabelLong.toLowerCase()} growth rate - this indicates market trends and future opportunities.\n\nFastest Growing: ${data.fastestGrowingType} at ${data.fastestGrowthRate}% ${growthLabelShort}\n\nWhat's driving ${data.fastestGrowingType} growth:\n\n${data.fastestGrowingType === "VRI" ? `📹 VRI GROWTH DRIVERS:\n• Technology maturation: Better platforms, easier to use\n• Cost savings: Clients reducing on-site interpretation budgets\n• COVID-19 legacy: Permanent shift to remote/hybrid work\n• Telehealth boom: Healthcare going virtual = need for video interpretation\n• Accessibility: Clients can access interpreters anywhere, anytime\n\nMarket opportunity:\n• VRI market projected to grow 15-20% annually through 2028\n• Healthcare, government, education leading adopters\n• Potential to cannibalize some On-Site work but expanding total market\n\nCapacity planning:\n• Train on-site interpreters to also do VRI (cross-training)\n• Invest in VRI platform capabilities (better video quality, screen sharing, document viewers)\n• Build scheduler flexibility to handle both scheduled and on-demand VRI\n• Market VRI as "on-site quality at remote prices"` : ""}\n\n${data.fastestGrowingType === "Translation" ? `📝 TRANSLATION GROWTH DRIVERS:\n• Globalization: Companies expanding internationally need translated content\n• Regulatory requirements: Healthcare, legal, financial industries require translation\n• Digital content explosion: Websites, apps, marketing materials need multilingual versions\n• E-commerce growth: Online retailers selling in multiple markets\n• Specialization premium: Demand for certified, specialized translation (medical, legal, technical)\n\nMarket opportunity:\n• Translation industry growing 7-10% annually\n• Machine translation creates MORE demand (clients realize quality matters)\n• Niche specializations command 50-100% premium pricing\n\nCapacity planning:\n• Recruit translators in high-demand languages and specialties\n• Invest in translation technology (CAT tools, translation memory)\n• Develop industry vertical expertise (healthcare, legal, financial)\n• Offer premium services (rush, certification, desktop publishing)` : ""}\n\nStrategic implications:\n\n✅ INVEST IN GROWTH:\n• Allocate marketing budget to fastest-growing service\n• Hire/train capacity in this area BEFORE you need it\n• Build operational excellence while growing (don't sacrifice quality for growth)\n• Establish yourself as market leader while market is hot\n\n⚠️ RISKS TO MANAGE:\n• Fast growth can strain operations (quality issues, service delays)\n• May need to invest in infrastructure (technology, management, training)\n• Don't neglect other services - maintain balanced portfolio\n• Growth rates eventually normalize - plan for maturation\n\nAction items:\n1. Understand WHY this service is growing (market trend or your specific success?)\n2. Ensure you have capacity to meet demand (linguists, technology, processes)\n3. Capitalize on momentum with targeted marketing\n4. Monitor quality metrics closely during growth\n5. Consider if you can replicate growth drivers in other service types`,
            },
          ],
        },
      ];
    },
    [
      handleServiceTypeClick,
      growthLabelLong,
      growthLabelShort,
      isSingleMonthSelection,
    ],
  );

  const chartsConfig = useCallback(
    (data) => {
      const handlePieClick = (item) => {
        const categoryMap = {
          1: "Translation",
          2: "Interpretation",
          3: "Other Services",
        };

        const category = categoryMap[item.id];
        if (category) {
          handleServiceTypeClick(category, data);
        }
      };

      return (
        <Grid container spacing={3}>
          {/* Service Type Distribution */}
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Revenue Distribution by Service Type"
              tooltipTitle="Service Mix Portfolio (Revenue Breakdown)"
              tooltipContent={`This chart shows what percentage of your revenue comes from each major service category.\n\nWhy service mix matters:\n\n1. RISK MANAGEMENT:\n   • Over-reliance on one service type = vulnerability if that market declines\n   • Diversified mix = more stable, resilient business\n   • Example: If 90% is Interpretation and demand shifts to Translation, you're exposed\n\n2. PROFITABILITY INSIGHTS:\n   • Translation: Typically highest margin, scalable, project-based\n   • Interpretation: Premium pricing but complexity varies (On-Site vs OPI)\n   • Other Services: Specialized offerings, often bundled with core services\n\n3. CAPACITY PLANNING:\n   • Each service needs different resources:\n     - Translation: Specialized translators, quality review process, potentially translation memory tools\n     - Interpretation: Real-time linguist availability, may need technology platforms (VRI/OPI) or travel logistics (On-Site)\n     - Other Services: Varies by service type (equipment, specialized skills, etc.)\n\n4. MARKET POSITIONING:\n   • Your mix shapes your brand:\n     - Heavy Translation = Content localization specialist\n     - Heavy Interpretation = Real-time language access provider\n     - Balanced = Full-service language solutions\n\n5. GROWTH STRATEGY:\n   • Identify underutilized service types - cross-sell to existing clients\n   • Example: Translation clients may not know you offer interpretation\n   • Balanced growth across types vs doubling down on strengths?\n\nHealthy portfolio indicators:\n🟢 No single service >70% (unless strategic specialization)\n🟢 At least 2 major service types generating meaningful revenue\n🟢 Growing service types have operational capacity to support demand\n\nRed flags:\n🔴 90%+ from one service = Single point of failure\n🔴 Declining share in core services = Competitive pressure\n🔴 Growing service types lack capacity = Quality/delivery issues ahead\n\nClick any segment to drill into detailed performance metrics and see all sub-types within that category.`}
              data={[
                {
                  id: 1,
                  label: `Translation (${data.translationCount || 0})`,
                  value: data.translationRevenue || 0,
                },
                {
                  id: 2,
                  label: `Interpretation (${data.interpretationCount || 0})`,
                  value: data.interpretationRevenue || 0,
                },
                {
                  id: 3,
                  label: `Other Services (${data.otherServicesCount || 0})`,
                  value: data.otherServicesRevenue || 0,
                },
              ].filter((item) => item.value > 0)}
              height={350}
              showLegend={true}
              valueFormatter={(value) =>
                value != null
                  ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "$0.00"
              }
              onSliceClick={handlePieClick}
            />
          </Grid>

          {/* Service Type Trend */}
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportLineChart
              title="Service Type Revenue Trend (6 Months)"
              tooltipTitle="Service Revenue Trends Over Time"
              tooltipContent={`Shows how revenue from each major service category has trended over the last 6 months.\n\nHow to read this chart:\n• Each line represents a different service category\n• Lines trending UP = Growing service type (increasing demand, successful marketing, or price increases)\n• Lines trending DOWN = Declining service type (market headwinds, competition, or seasonal patterns)\n• Lines crossing = Shift in service mix\n\nWhat to look for:\n\n📈 GROWTH PATTERNS:\n• Steady upward slope = Sustainable, healthy growth\n• Steep climb = Rapid growth (exciting but ensure you can sustain it)\n• Flat = Mature, stable service (predictable but limited growth)\n• Declining = Warning sign (investigate why)\n\n🔄 MIX SHIFTS:\n• Interpretation line rising while Translation falling = Shift toward real-time services\n• Translation growing faster than Interpretation = Content localization demand\n• All lines rising together = Overall business growth (ideal!)\n\n📊 SEASONALITY:\n• Do certain service types peak at specific times?\n  - Translation may spike before product launches or regulatory deadlines\n  - Interpretation may have different patterns (healthcare seasonal, legal cyclical)\n  - Other Services may correlate with events/conferences\n\n💡 STRATEGIC INSIGHTS:\n\nIf Translation is growing fastest:\n→ Invest in translator recruitment, CAT tools, build specialization in profitable verticals\n\nIf Interpretation is declining:\n→ Is this temporary or structural? May need to adapt (shift On-Site to VRI, improve pricing)\n\nIf all lines are flat:\n→ Need new growth initiatives - new markets, new services, pricing strategy, marketing push\n\nAction based on trends:\n• Growing services: Ensure capacity, invest in infrastructure, hire ahead of demand\n• Declining services: Investigate root cause, consider pivoting or repositioning\n• Stable services: Optimize efficiency, focus on profitability over growth\n\nUse this chart to predict future capacity needs and resource allocation 3-6 months ahead.`}
              xAxisData={data.monthlyTrend?.months || []}
              series={[
                {
                  data: data.monthlyTrend?.translation || [],
                  label: "Translation",
                  curve: "monotoneX",
                  color: chartColors.semantic.secondary,
                },
                {
                  data: data.monthlyTrend?.interpretation || [],
                  label: "Interpretation",
                  curve: "monotoneX",
                  color: chartColors.semantic.primary,
                },
                {
                  data: data.monthlyTrend?.otherServices || [],
                  label: "Other Services",
                  curve: "monotoneX",
                  color: chartColors.semantic.info,
                },
              ]}
              height={350}
              valueFormatter={(value) =>
                value
                  ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : ""
              }
            />
          </Grid>

          {/* Service Volume Comparison */}
          <Grid size={{ xs: 12 }}>
            <ReportBarChart
              title="Service Volume & Average Value by Category"
              tooltipTitle="Service Count vs Average Value Analysis"
              tooltipContent={`Compares service volume (how many services) vs average revenue per service across the three main service categories.\n\nWhy this comparison matters:\n\n🔢 VOLUME (Service Count):\n• Shows how much WORK each category generates\n• High volume = Busy, operationally demanding\n• Interpretation typically higher volume (many daily calls/sessions)\n• Translation may be lower volume but longer-duration projects\n\n💰 AVERAGE VALUE (Revenue per Service):\n• Shows how much REVENUE each service generates on average\n• High value = Premium pricing, complex work, or longer duration\n• Translation often has higher avg value (project-based)\n• Interpretation avg varies widely (On-Site high, OPI low)\n\nFour quadrants of strategy:\n\n📊 HIGH VOLUME + HIGH VALUE (ideal but rare):\n• Example: If Translation had many projects at high $ each\n• Goal: Maximize capacity in this quadrant\n• These are your core profit drivers\n\n🚀 LOW VOLUME + HIGH VALUE:\n• Example: Specialized translation (legal, medical certifications)\n• Strategy: Great margins, try to increase volume through marketing\n• Marketing opportunity: Why aren't we doing more of these?\n• Resource efficient: Few services, high revenue\n\n📈 HIGH VOLUME + LOW VALUE:\n• Example: OPI calls within Interpretation category\n• Strategy: Need SCALE to be profitable\n• Focus on operational efficiency, automation, platform technology\n• Profitability comes from volume, not per-service margin\n\n⚠️ LOW VOLUME + LOW VALUE:\n• If any category falls here, question strategy\n• May be strategic (market entry, client requirement) but watch closely\n• Consider pricing increases, service improvements, or discontinuing\n\nKey insights:\n\n1. PRICING POWER:\n   • If average value is declining, you're either:\n     a) Discounting too much to win business\n     b) Shifting mix toward lower-value sub-types\n     c) Facing competitive pricing pressure\n\n2. CAPACITY PLANNING:\n   • High-volume categories need:\n     - More linguists and faster recruiting\n     - Better scheduling/routing systems\n     - Streamlined processes and automation\n   • High-value categories need:\n     - Top-tier linguists with specialized expertise\n     - Rigorous quality control\n     - Dedicated relationship management\n\n3. PROFITABILITY:\n   • Revenue = Volume × Average Value\n   • To grow revenue, must increase EITHER:\n     a) Volume (deliver more services)\n     b) Average Value (charge more per service)\n     c) Both (ideal but hardest!)\n\n4. MIX OPTIMIZATION:\n   • Within Interpretation: Balance high-value On-Site with high-volume OPI\n   • Within Translation: Seek specialized niches that command premium pricing\n   • Within Other Services: Identify which are worth promoting vs phasing out\n\nExample optimization:\n• Translation: 156 projects at $309 avg = $48,230 revenue\n• If you increased avg value to $350 (13% price increase): $54,600 = +$6,370 revenue with SAME volume\n• If you increased volume to 180 with same $309: $55,620 = +$7,390 revenue with SAME pricing\n\nBest strategy: Focus on increasing average value in high-volume categories = biggest revenue impact with least operational burden.\n\nClick any bar to see detailed sub-type breakdown within that category.`}
              data={[
                {
                  type: "Translation",
                  count: data.translationCount || 0,
                  avgValue: data.translationAvgValue || 0,
                },
                {
                  type: "Interpretation",
                  count: data.interpretationCount || 0,
                  avgValue: data.interpretationAvgValue || 0,
                },
                {
                  type: "Other Services",
                  count: data.otherServicesCount || 0,
                  avgValue: data.otherServicesAvgValue || 0,
                },
              ]}
              xAxisKey="type"
              series={[
                {
                  dataKey: "count",
                  label: "Service Count",
                  color: chartColors.semantic.info,
                },
                {
                  dataKey: "avgValue",
                  label: "Avg Value ($)",
                  color: chartColors.semantic.secondary,
                },
              ]}
              height={350}
              valueFormatter={(value) =>
                value != null
                  ? value.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "0"
              }
            />
          </Grid>
        </Grid>
      );
    },
    [
      chartColors.semantic.info,
      chartColors.semantic.primary,
      chartColors.semantic.secondary,
      handleServiceTypeClick,
    ],
  );

  return (
    <>
      <DashboardReport
        title="Revenue by Service Type"
        description={`Comprehensive service type performance analysis with revenue breakdown, ${growthLabelShort} growth comparisons, service volume metrics, profitability analysis, trend tracking, and portfolio diversification insights.`}
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
        useBarChart={drillDown.useBarChart}
      />
    </>
  );
};

/**
 * Revenue by Language Report - Table showing revenue by language pair
 */
export const RevenueByLanguage = ({ filters }) => {
  const columns = [
    {
      field: "language",
      headerName: "Language",
      width: 220,
    },
    {
      field: "servicesCount",
      headerName: "Services",
      width: 110,
    },
    {
      field: "revenue",
      headerName: "Revenue",
      width: 150,
      renderCell: (row) =>
        `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: "cost",
      headerName: "Cost",
      width: 140,
      renderCell: (row) =>
        `$${(row.cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: "profit",
      headerName: "Profit",
      width: 150,
      renderCell: (row) =>
        `$${(row.profit || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: "profitMargin",
      headerName: "Margin %",
      width: 120,
      renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
    },
  ];

  const dataFetchFn = useCallback(async (filters) => {
    const response = await api.get("/reports/financial/revenue-by-language", {
      params: filters,
    });

    return {
      data: response.data?.data || { tableData: [] },
    };
  }, []);

  return (
    <DataTableReport
      title="Revenue by Language"
      description="Revenue breakdown by language pairs, identifying the most profitable languages."
      columns={columns}
      dataFetchFn={dataFetchFn}
      filters={filters}
    />
  );
};

/**
 * Profit Margin Analysis Report - Dashboard showing profit calculations
 */
export const ProfitMargin = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = () => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  };

  const formatCurrency = (value) =>
    `$${(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatPercent = (value) => formatPercentValue(value, { digits: 2 });

  const buildMonthlyRows = (data) => {
    const months = data.monthlyData?.months || [];
    return months.map((month, idx) => {
      const revenue = data.monthlyData?.revenue?.[idx] || 0;
      const costs = data.monthlyData?.costs?.[idx] || 0;
      const grossProfit = data.monthlyData?.profit?.[idx] || 0;
      const margin = data.monthlyData?.margin?.[idx] || 0;
      const servicesCount = data.monthlyData?.servicesCount?.[idx] || 0;
      const translationRevenue = data.monthlyData?.translationRevenue?.[idx] || 0;
      const interpretationRevenue = data.monthlyData?.interpretationRevenue?.[idx] || 0;
      const otherServicesRevenue = data.monthlyData?.otherServicesRevenue?.[idx] || 0;
      const translationCosts = data.monthlyData?.translationCosts?.[idx] || 0;
      const interpretationCosts = data.monthlyData?.interpretationCosts?.[idx] || 0;
      const otherServicesCosts = data.monthlyData?.otherServicesCosts?.[idx] || 0;
      const translationGrossProfit = data.monthlyData?.translationProfit?.[idx] || 0;
      const interpretationGrossProfit = data.monthlyData?.interpretationProfit?.[idx] || 0;
      const otherServicesGrossProfit = data.monthlyData?.otherServicesProfit?.[idx] || 0;
      const translationServicesCount = data.monthlyData?.translationServicesCount?.[idx] || 0;
      const interpretationServicesCount = data.monthlyData?.interpretationServicesCount?.[idx] || 0;
      const otherServicesCount = data.monthlyData?.otherServicesCount?.[idx] || 0;

      const prevRevenue = idx > 0 ? (data.monthlyData?.revenue?.[idx - 1] || 0) : 0;
      const prevCost = idx > 0 ? (data.monthlyData?.costs?.[idx - 1] || 0) : 0;
      const prevProfit = idx > 0 ? (data.monthlyData?.profit?.[idx - 1] || 0) : 0;

      const momRevenue = idx > 0 && prevRevenue !== 0 ? ((revenue - prevRevenue) / Math.abs(prevRevenue)) * 100 : 0;
      const momCost = idx > 0 && prevCost !== 0 ? ((costs - prevCost) / Math.abs(prevCost)) * 100 : 0;
      const momGrossProfit = idx > 0 && prevProfit !== 0 ? ((grossProfit - prevProfit) / Math.abs(prevProfit)) * 100 : 0;

      return {
        month,
        revenue,
        costs,
        grossProfit,
        margin,
        servicesCount,
        translationRevenue,
        interpretationRevenue,
        otherServicesRevenue,
        translationCosts,
        interpretationCosts,
        otherServicesCosts,
        translationGrossProfit,
        interpretationGrossProfit,
        otherServicesGrossProfit,
        translationServicesCount,
        interpretationServicesCount,
        otherServicesCount,
        momRevenue,
        momCost,
        momGrossProfit,
      };
    });
  };

  const handleRevenueClick = useCallback((data) => {
    const monthlyRows = buildMonthlyRows(data);
    const bestMonth = monthlyRows.reduce((best, current) =>
      current.revenue > (best?.revenue || 0) ? current : best,
      monthlyRows[0]
    );

    setDrillDown({
      open: true,
      title: "Total Revenue - Detailed Breakdown",
      subtitle: `${monthlyRows.length} months • YoY ${formatPercentValue(data.yoyRevenueGrowth, { digits: 2, signed: true })}`,
      description: "Month-by-month revenue trend with MoM movement and service volume context.",
      data: monthlyRows,
      columns: [
        { field: "month", headerName: "Month", width: 130 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 140,
          renderCell: (row) => formatCurrency(row.revenue),
        },
        {
          field: "servicesCount",
          headerName: "Services",
          width: 100,
        },
        {
          field: "margin",
          headerName: "Gross Margin",
          width: 120,
          renderCell: (row) => formatPercent(row.margin),
        },
      ],
      summary: {
        metrics: [
          { label: "Total Revenue", value: formatCurrency(data.totalRevenue), color: "success.main" },
          { label: "YoY Growth", value: formatPercentValue(data.yoyRevenueGrowth, { digits: 2, signed: true }), color: isComparable(data.yoyRevenueGrowth) ? (data.yoyRevenueGrowth >= 0 ? "success.main" : "error.main") : "text.secondary" },
          { label: "Best Month", value: bestMonth ? `${bestMonth.month} • ${formatCurrency(bestMonth.revenue)}` : "N/A" },
        ],
      },
    });
  }, []);

  const handleCostClick = useCallback((data) => {
    const monthlyRows = buildMonthlyRows(data);
    const highestCostMonth = monthlyRows.reduce((worst, current) =>
      current.costs > (worst?.costs || 0) ? current : worst,
      monthlyRows[0]
    );

    setDrillDown({
      open: true,
      title: "Cost of Revenue - Detailed Breakdown",
      subtitle: `${monthlyRows.length} months • YoY ${formatPercentValue(data.yoyCostGrowth, { digits: 2, signed: true })}`,
      description: "Direct delivery costs by month with change rates and cost intensity.",
      data: monthlyRows,
      columns: [
        { field: "month", headerName: "Month", width: 130 },
        {
          field: "costs",
          headerName: "Cost of Revenue",
          width: 170,
          renderCell: (row) => formatCurrency(row.costs),
        },
        {
          field: "servicesCount",
          headerName: "Services",
          width: 100,
        },
        {
          field: "costPerService",
          headerName: "Cost / Service",
          width: 130,
          renderCell: (row) => formatCurrency((row.costs || 0) / (row.servicesCount || 1)),
        },
      ],
      summary: {
        metrics: [
          { label: "Cost of Revenue", value: formatCurrency(data.costOfRevenue ?? data.totalCosts), color: "error.main" },
          { label: "YoY Change", value: formatPercentValue(data.yoyCostGrowth, { digits: 2, signed: true }), color: isComparable(data.yoyCostGrowth) ? (data.yoyCostGrowth <= 0 ? "success.main" : "error.main") : "text.secondary" },
          { label: "Highest Cost Month", value: highestCostMonth ? `${highestCostMonth.month} • ${formatCurrency(highestCostMonth.costs)}` : "N/A" },
        ],
      },
    });
  }, []);

  const handleGrossProfitClick = useCallback((data) => {
    const monthlyRows = buildMonthlyRows(data);
    const bestMonth = monthlyRows.reduce((best, current) =>
      current.grossProfit > (best?.grossProfit || 0) ? current : best,
      monthlyRows[0]
    );
    const worstMonth = monthlyRows.reduce((worst, current) =>
      current.grossProfit < (worst?.grossProfit || Infinity) ? current : worst,
      monthlyRows[0]
    );
    const bestMarginMonth = monthlyRows.reduce((best, current) =>
      current.margin > (best?.margin ?? Number.NEGATIVE_INFINITY) ? current : best,
      monthlyRows[0]
    );
    const worstMarginMonth = monthlyRows.reduce((worst, current) =>
      current.margin < (worst?.margin ?? Number.POSITIVE_INFINITY) ? current : worst,
      monthlyRows[0]
    );

    setDrillDown({
      open: true,
      title: "Gross Profit - Detailed Breakdown",
      subtitle: `${monthlyRows.length} months • YoY ${formatPercentValue(data.yoyGrossProfitGrowth, { digits: 2, signed: true })}`,
      description: "Monthly gross profit performance. Click any month row to see pie charts for Gross Profit by service type and Services Count by type.",
      data: monthlyRows,
      columns: [
        { field: "month", headerName: "Month", width: 130 },
        {
          field: "grossProfit",
          headerName: "Gross Profit",
          width: 140,
          renderCell: (row) => formatCurrency(row.grossProfit),
        },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 130,
          renderCell: (row) => formatCurrency(row.revenue),
        },
        {
          field: "costs",
          headerName: "Cost of Revenue",
          width: 150,
          renderCell: (row) => formatCurrency(row.costs),
        },
        {
          field: "margin",
          headerName: "Gross Margin",
          width: 120,
          renderCell: (row) => formatPercent(row.margin),
        },
        { field: "servicesCount", headerName: "Services", width: 100 },
      ],
      summary: {
        metrics: [
          { label: "Gross Profit", value: formatCurrency(data.totalProfit), color: (data.totalProfit || 0) >= 0 ? "success.main" : "error.main" },
          { label: "Gross Margin", value: formatPercent(data.avgProfitMargin), color: (data.avgProfitMargin || 0) >= 0 ? "success.main" : "error.main" },
          { label: "YoY Growth", value: formatPercentValue(data.yoyGrossProfitGrowth, { digits: 2, signed: true }), color: isComparable(data.yoyGrossProfitGrowth) ? (data.yoyGrossProfitGrowth >= 0 ? "success.main" : "error.main") : "text.secondary" },
          { label: "Best Month", value: bestMonth ? `${bestMonth.month} • ${formatCurrency(bestMonth.grossProfit)}` : "N/A", color: "success.main" },
          { label: "Worst Month", value: worstMonth ? `${worstMonth.month} • ${formatCurrency(worstMonth.grossProfit)}` : "N/A", color: "error.main" },
          { label: "Best Margin Month", value: bestMarginMonth ? `${bestMarginMonth.month} • ${formatPercent(bestMarginMonth.margin)}` : "N/A", color: "success.main" },
          { label: "Worst Margin Month", value: worstMarginMonth ? `${worstMarginMonth.month} • ${formatPercent(worstMarginMonth.margin)}` : "N/A", color: "error.main" },
        ],
      },
    });
  }, []);

  const handleProfitQualityClick = useCallback((data) => {
    setDrillDown({
      open: true,
      title: "Profit Quality - Service Distribution",
      subtitle: `${(data.marginBandData || []).reduce((sum, b) => sum + (b.servicesCount || 0), 0)} total services`,
      description: "Service mix by margin band to identify where profitability risk and upside are concentrated.",
      data: (data.marginBandData || []).map((b) => ({
        marginBand: b.band,
        servicesCount: b.servicesCount,
        revenue: b.revenue,
        costs: b.costs,
        grossProfit: b.profit,
        grossMargin: b.avgMargin,
      })),
      columns: [
        { field: "marginBand", headerName: "Margin Band", width: 180 },
        { field: "servicesCount", headerName: "Services", width: 100 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 130,
          renderCell: (row) => formatCurrency(row.revenue),
        },
        {
          field: "costs",
          headerName: "Cost of Revenue",
          width: 150,
          renderCell: (row) => formatCurrency(row.costs),
        },
        {
          field: "grossProfit",
          headerName: "Gross Profit",
          width: 130,
          renderCell: (row) => formatCurrency(row.grossProfit),
        },
        {
          field: "grossMargin",
          headerName: "Gross Margin",
          width: 120,
          renderCell: (row) => formatPercent(row.grossMargin),
        },
      ],
      summary: {
        metrics: [
          { label: "Profitable Services", value: `${data.profitabilityMix?.profitableServices?.toLocaleString() || 0}`, color: "success.main" },
          { label: "Loss-Making Services", value: `${data.profitabilityMix?.lossMakingServices?.toLocaleString() || 0}`, color: "error.main" },
          { label: "Profitable Rate", value: `${(data.profitabilityMix?.profitableRate || 0).toFixed(1)}%`, color: "success.main" },
          { label: "Loss Rate", value: `${(data.profitabilityMix?.lossRate || 0).toFixed(1)}%`, color: "error.main" },
        ],
      },
    });
  }, []);

  const dataFetchFn = useCallback(async (filters) => {
    const response = await api.get("/reports/financial/profit-margin", {
      params: filters,
    });
    return { data: response.data?.data || response.data || {} };
  }, []);

  const metricsConfig = (data) => [
    {
      groupTitle: "Core Profitability",
      metrics: [
        {
          title: "Total Revenue",
          value: `$${(data.totalRevenue || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: <AttachMoney sx={{ fontSize: 32 }} />,
          trend: data.yoyRevenueGrowth,
          trendLabel: "YoY growth",
          onClick: () => handleRevenueClick(data),
        },
        {
          title: "Cost of Revenue",
          value: `$${((data.costOfRevenue ?? data.totalCosts) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: <AccountBalance sx={{ fontSize: 32 }} />,
          color: "error.main",
          subtitle: data.totalRevenue > 0 ? `${formatPercent((((data.costOfRevenue ?? data.totalCosts) || 0) / data.totalRevenue) * 100)} of revenue` : "0.00% of revenue",
          onClick: () => handleCostClick(data),
        },
        {
          title: "Gross Profit",
          value: `$${(data.totalProfit || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          subtitle: `${(data.avgProfitMargin || 0).toFixed(2)}% gross margin`,
          icon: <TrendingUp sx={{ fontSize: 32 }} />,
          color: (data.totalProfit || 0) >= 0 ? "success.main" : "error.main",
          onClick: () => handleGrossProfitClick(data),
        },
      ],
    },
    {
      groupTitle: "Profit Quality",
      metrics: [
        {
          title: "Profitable Services",
          value: `${data.profitabilityMix?.profitableServices?.toLocaleString() || "0"}`,
          subtitle: `${(data.profitabilityMix?.profitableRate || 0).toFixed(1)}% of services`,
          color: "success.main",
          onClick: () => handleProfitQualityClick(data),
        },
        {
          title: "Loss-Making Services",
          value: `${data.profitabilityMix?.lossMakingServices?.toLocaleString() || "0"}`,
          subtitle: `${(data.profitabilityMix?.lossRate || 0).toFixed(1)}% of services`,
          color: "error.main",
          onClick: () => handleProfitQualityClick(data),
        },
        {
          title: "Break-Even Services",
          value: `${data.profitabilityMix?.breakEvenServices?.toLocaleString() || "0"}`,
          subtitle: "Near-zero profit services",
          onClick: () => handleProfitQualityClick(data),
        },
      ],
    },
  ];

  const chartsConfig = (data) => {
    const monthlyRows = (data.monthlyData?.months || []).map((month, idx) => ({
      month,
      revenue: data.monthlyData?.revenue?.[idx] || 0,
      costs: data.monthlyData?.costs?.[idx] || 0,
      profit: data.monthlyData?.profit?.[idx] || 0,
      margin: data.monthlyData?.margin?.[idx] || 0,
      servicesCount: data.monthlyData?.servicesCount?.[idx] || 0,
    }));

    const categoryRows = (data.categoryMarginData || []).map((c) => ({
      category: c.category,
      avgMargin: c.avgMargin || 0,
      revenue: c.revenue || 0,
      profit: c.profit || 0,
      servicesCount: c.servicesCount || 0,
    }));

    const marginBandRows = (data.marginBandData || []).map((b, index) => ({
      id: index + 1,
      label: b.band,
      value: b.servicesCount || 0,
    }));

    return (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportLineChart
            title="Monthly Gross Margin Trend"
            xAxisData={monthlyRows.map((m) => m.month)}
            series={[
              {
                data: monthlyRows.map((m) => m.margin),
                label: "Margin %",
                curve: "monotoneX",
                color: chartColors.success,
                yAxisId: "marginAxis",
                valueFormatter: (value) => `${(value || 0).toFixed(2)}%`,
              },
              {
                data: monthlyRows.map((m) => m.profit),
                label: "Gross Profit ($)",
                curve: "linear",
                color: chartColors.info,
                yAxisId: "profitAxis",
                valueFormatter: (value) =>
                  `$${(value || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
              },
            ]}
            height={350}
            chartMargin={{ top: 20, right: 120, bottom: 50, left: 90 }}
            leftAxis="profitAxis"
            rightAxis="marginAxis"
            yAxisConfigs={[
              {
                id: "profitAxis",
                position: "left",
                width: 70,
                min: 0,
                domainLimit: "strict",
                valueFormatter: (value) => `$${(value || 0).toLocaleString()}`,
              },
              {
                id: "marginAxis",
                position: "right",
                width: 60,
                min: 0,
                max: 100,
                valueFormatter: (value) => `${(value || 0).toFixed(0)}%`,
              },
            ]}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Margin by Service Category"
            data={categoryRows}
            xAxisKey="category"
            series={[
              {
                dataKey: "avgMargin",
                label: "Avg Margin %",
                color: chartColors.warning,
              },
            ]}
            height={350}
            yAxisLabel="Margin %"
            yAxisScaleType="linear"
            valueFormatter={(value) => `${(value || 0).toFixed(2)}%`}
            tooltipTitle="Category Margin"
            tooltipContent="Compares average profit margins across Translation, Interpretation, and Other services for the selected period."
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Service Margin Distribution"
            data={marginBandRows}
            height={350}
            showLabels={false}
            showLegend={true}
            legendPosition="right"
            valueFormatter={(value) =>
              `${value?.toLocaleString() || 0} services`
            }
            tooltipTitle="Margin Bands"
            tooltipContent="Shows how many services fall into each margin band to highlight profitability quality and risk concentration."
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportLineChart
            title="Monthly Margin vs Service Volume"
            xAxisData={monthlyRows.map((m) => m.month)}
            series={[
              {
                label: "Margin %",
                data: monthlyRows.map((m) => m.margin),
                curve: "monotoneX",
                color: chartColors.success,
                yAxisId: "marginAxis2",
                valueFormatter: (value) => `${(value || 0).toFixed(2)}%`,
              },
              {
                label: "Service Count",
                data: monthlyRows.map((m) => m.servicesCount),
                curve: "linear",
                color: chartColors.warning,
                yAxisId: "volumeAxis2",
                valueFormatter: (value) => `${(value || 0).toLocaleString()}`,
              },
            ]}
            height={350}
            chartMargin={{ top: 20, right: 120, bottom: 50, left: 90 }}
            leftAxis="volumeAxis2"
            rightAxis="marginAxis2"
            yAxisConfigs={[
              {
                id: "volumeAxis2",
                position: "left",
                width: 70,
                min: 0,
                valueFormatter: (value) => `${(value || 0).toLocaleString()}`,
              },
              {
                id: "marginAxis2",
                position: "right",
                width: 60,
                min: 0,
                max: 100,
                valueFormatter: (value) => `${(value || 0).toFixed(0)}%`,
              },
            ]}
            tooltipTitle="Margin vs Volume"
            tooltipContent="Shows whether higher service volume months maintain healthy margins, helping spot scale-efficiency or margin pressure over time."
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <DashboardReport
        title="Profit Margin Analysis"
        description="Detailed gross margin performance analysis with profitability mix, margin bands, category-level margins, and top/bottom client margin signals."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Service Profitability Report - Table showing profit per service
 */
export const ServiceProfitability = ({ filters }) => {
  const theme = useTheme();
  const columns = [
    {
      field: "serviceType",
      headerName: "Service Type",
      width: 240,
    },
    {
      field: "servicesCount",
      headerName: "Services",
      width: 110,
    },
    {
      field: "revenue",
      headerName: "Revenue",
      width: 120,
      renderCell: (row) =>
        `$${row.revenue?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: "cost",
      headerName: "Cost",
      width: 120,
      renderCell: (row) =>
        `$${row.cost?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: "profit",
      headerName: "Profit",
      width: 140,
      renderCell: (row) => (
        <span
          style={{
            color:
              (row.profit || 0) >= 0
                ? theme.palette.success.main
                : theme.palette.error.main,
          }}
        >
          $
          {(row.profit || 0).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      field: "profitMargin",
      headerName: "Margin %",
      width: 110,
      renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
    },
    {
      field: "avgProfitPerService",
      headerName: "Avg Profit / Service",
      width: 160,
      renderCell: (row) =>
        `$${(row.avgProfitPerService || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
  ];

  const dataFetchFn = useCallback(async (filters) => {
    const response = await api.get("/reports/financial/service-profitability", {
      params: filters,
    });

    return {
      data: response.data?.data || { tableData: [] },
    };
  }, []);

  return (
    <DataTableReport
      title="Service Profitability"
      description="Individual service-level profitability analysis showing which services are most/least profitable."
      columns={columns}
      dataFetchFn={dataFetchFn}
      filters={filters}
    />
  );
};

/**
 * Payment Method Analysis Report - Table showing revenue by payment method
 */
export const PaymentMethod = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const formatCurrency = useCallback(
    (value) =>
      `$${(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [],
  );

  const dataFetchFn = useCallback(async (activeFilters) => {
    const [paymentMethodsResponse, paymentRangesResponse, outstandingResponse] =
      await Promise.all([
        api.get("/reports/financial/payment-methods", { params: activeFilters }),
        api.get("/reports/financial/payment-ranges", { params: activeFilters }),
        api.get("/reports/invoice/outstanding", { params: activeFilters }),
      ]);

    const paymentMethods = paymentMethodsResponse.data?.data || {};
    const paymentRanges = paymentRangesResponse.data?.data || {};
    const outstanding = outstandingResponse.data?.data || {};

    const methodsRows = (paymentMethods.tableData || []).map((row, index) => ({
      id: index + 1,
      ...row,
    }));

    const rangesRows = (paymentRanges.ranges || []).map((range) => ({
      ...range,
      avgPaymentDays:
        range.clients && range.clients.length > 0
          ? Math.round(
              range.clients.reduce(
                (sum, client) => sum + (client.avgPaymentDays || 0),
                0,
              ) / range.clients.length,
            )
          : 0,
    }));

    return {
      data: {
        methodsRows,
        methodsChart: paymentMethods.chartData || [],
        rangesRows,
        collectionSummary: outstanding.summary || {},
        outstandingInvoices: outstanding.invoices || [],
      },
    };
  }, []);

  const openPaymentMethodDrawer = useCallback(
    (data, selectedLabel = null) => {
      const rows = selectedLabel
        ? (data.methodsRows || []).filter(
            (row) => row.paymentMethod === selectedLabel,
          )
        : data.methodsRows || [];

      setDrillDown({
        open: true,
        title: selectedLabel
          ? `Payment Method: ${selectedLabel}`
          : "Payment Method Performance",
        subtitle: `${rows.length} method group${rows.length === 1 ? "" : "s"} • ${formatCurrency(
          rows.reduce((sum, row) => sum + (row.revenue || 0), 0),
        )} revenue`,
        description:
          "Payment methods ranked by revenue, fee drag, and average transaction size.",
        data: [...rows].sort((a, b) => (b.revenue || 0) - (a.revenue || 0)),
        columns: [
          { field: "paymentMethod", headerName: "Payment Method", width: 220 },
          {
            field: "revenue",
            headerName: "Revenue",
            width: 140,
            renderCell: (row) => formatCurrency(row.revenue),
          },
          {
            field: "fees",
            headerName: "Fees",
            width: 120,
            renderCell: (row) => formatCurrency(row.fees),
          },
          {
            field: "netRevenue",
            headerName: "Net Revenue",
            width: 150,
            renderCell: (row) => formatCurrency(row.netRevenue),
          },
          { field: "transactionsCount", headerName: "Transactions", width: 110 },
          {
            field: "avgTransactionAmount",
            headerName: "Avg Transaction",
            width: 150,
            renderCell: (row) => formatCurrency(row.avgTransactionAmount),
          },
        ],
      });
    },
    [formatCurrency],
  );

  const openPaymentRangeDrawer = useCallback(
    (data, selectedRange = null) => {
      const rangeRows = selectedRange
        ? (data.rangesRows || []).filter((row) => row.label === selectedRange)
        : data.rangesRows || [];

      const clientsRows = rangeRows.flatMap((range) =>
        (range.clients || []).map((client) => ({
          ...client,
          rangeLabel: range.label,
        })),
      );

      setDrillDown({
        open: true,
        title: selectedRange
          ? `Payment Timing: ${selectedRange}`
          : "Client Payment Behavior",
        subtitle: `${clientsRows.length} clients • ${formatCurrency(
          clientsRows.reduce((sum, row) => sum + (row.totalRevenue || 0), 0),
        )} exposure`,
        description:
          "Client-level payment speed grouped by average days from due date to payment/current date.",
        data: [...clientsRows].sort(
          (a, b) => (b.avgPaymentDays || 0) - (a.avgPaymentDays || 0),
        ),
        columns: [
          { field: "clientName", headerName: "Client", width: 260 },
          { field: "rangeLabel", headerName: "Range", width: 120 },
          {
            field: "avgPaymentDays",
            headerName: "Avg Days",
            width: 110,
            renderCell: (row) => `${row.avgPaymentDays ?? "-"} days`,
          },
          { field: "invoiceCount", headerName: "Invoices", width: 100 },
          {
            field: "totalRevenue",
            headerName: "Invoice Amount",
            width: 140,
            renderCell: (row) => formatCurrency(row.totalRevenue),
          },
          {
            field: "hasUnpaidInvoices",
            headerName: "Has Unpaid",
            width: 110,
            renderCell: (row) => (row.hasUnpaidInvoices ? "Yes" : "No"),
          },
        ],
      });
    },
    [formatCurrency],
  );

  const openCollectionsDrawer = useCallback(
    (data, mode) => {
      const rows = data.outstandingInvoices || [];
      let filtered = rows;
      let title = "Collections Ledger";
      let description =
        "Invoice-level collections details including due dates, outstanding amounts, and risk labels.";

      if (mode === "overdue") {
        filtered = rows.filter(
          (row) => row.daysPastDue != null && row.daysPastDue > 0,
        );
        title = "Overdue Collections";
      }

      if (mode === "critical") {
        filtered = rows.filter(
          (row) => row.daysPastDue != null && row.daysPastDue > 90,
        );
        title = "Critical Collections (90+ Days)";
      }

      setDrillDown({
        open: true,
        title,
        subtitle: `${filtered.length} invoices • ${formatCurrency(
          filtered.reduce(
            (sum, row) => sum + (row.outstandingAmount || 0),
            0,
          ),
        )} outstanding`,
        description,
        data: [...filtered].sort(
          (a, b) => (b.outstandingAmount || 0) - (a.outstandingAmount || 0),
        ),
        columns: [
          { field: "invoiceNumber", headerName: "Invoice #", width: 120 },
          { field: "clientName", headerName: "Client", width: 230 },
          {
            field: "dueDate",
            headerName: "Due Date",
            width: 130,
            renderCell: (row) =>
              row.dueDate
                ? new Date(row.dueDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "-",
          },
          {
            field: "daysPastDue",
            headerName: "Days Past Due",
            width: 120,
            renderCell: (row) =>
              row.daysPastDue == null ? "-" : `${row.daysPastDue} days`,
          },
          {
            field: "outstandingAmount",
            headerName: "Outstanding",
            width: 140,
            renderCell: (row) => formatCurrency(row.outstandingAmount),
          },
          {
            field: "riskLevel",
            headerName: "Risk",
            width: 110,
          },
          {
            field: "collectionProgress",
            headerName: "Collected %",
            width: 120,
            renderCell: (row) => `${(row.collectionProgress || 0).toFixed(1)}%`,
          },
        ],
      });
    },
    [formatCurrency],
  );

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.collectionSummary || {};
      const totalRevenue = (data.methodsRows || []).reduce(
        (sum, row) => sum + (row.revenue || 0),
        0,
      );
      const avgTransaction =
        (data.methodsRows || []).reduce(
          (sum, row) => sum + (row.avgTransactionAmount || 0),
          0,
        ) / Math.max((data.methodsRows || []).length, 1);

      return [
        {
          groupTitle: "Payment Methods",
          metrics: [
            {
              title: "Revenue via Payment Types",
              value: formatCurrency(totalRevenue),
              subtitle: `${(data.methodsRows || []).length} methods`,
              tooltipTitle: "Revenue via Payment Types",
              tooltipContent: `Total invoiced revenue segmented by payment method (card, ACH, cash, check, etc.).

Current period context:
- Total Revenue: ${formatCurrency(totalRevenue)}
- Payment Methods Tracked: ${(data.methodsRows || []).length}

How to use:
- Validate payment-channel mix health
- Detect over-reliance on one payment rail
- Pair with fee and net revenue metrics to optimize margin`,
              onClick: () => openPaymentMethodDrawer(data),
            },
            {
              title: "Avg Transaction Size",
              value: formatCurrency(avgTransaction),
              subtitle: "Across payment methods",
              tooltipTitle: "Average Transaction Size",
              tooltipContent: `Average invoice transaction value across all payment methods.

Formula:
Average Transaction = Sum of Avg Transaction by Method / Number of Methods

Current period context:
- Average Transaction: ${formatCurrency(avgTransaction)}

How to interpret:
- Rising average can indicate stronger pricing power or larger accounts
- Falling average may indicate smaller-job mix shift or discount pressure

Use this with payment mix to ensure growth quality, not just volume.`,
              onClick: () => openPaymentMethodDrawer(data),
            },
            {
              title: "Highest Method Share",
              value:
                data.methodsRows?.[0]?.paymentMethod || "Not available",
              subtitle: formatCurrency(data.methodsRows?.[0]?.revenue || 0),
              tooltipTitle: "Highest Method Share",
              tooltipContent: `Top-performing payment method by revenue contribution in the current filter scope.

Current period context:
- Top Method: ${data.methodsRows?.[0]?.paymentMethod || "Not available"}
- Top Method Revenue: ${formatCurrency(data.methodsRows?.[0]?.revenue || 0)}

Risk lens:
- Heavy concentration on one method creates operational and processing risk
- Balanced distribution improves resilience during payment rail disruptions

Click to view full method breakdown and compare fees vs net revenue.`,
              color: "info.main",
              onClick: () =>
                data.methodsRows?.[0]
                  ? openPaymentMethodDrawer(data, data.methodsRows[0].paymentMethod)
                  : openPaymentMethodDrawer(data),
            },
          ],
        },
        {
          groupTitle: "Collections Health",
          metrics: [
            {
              title: "Outstanding AR",
              value: formatCurrency(summary.totalOutstandingAmount || 0),
              subtitle: `${summary.totalOutstandingInvoices || 0} open invoices`,
              tooltipTitle: "Outstanding AR",
              tooltipContent: `Total unpaid accounts receivable currently open.

Current period context:
- Outstanding Amount: ${formatCurrency(summary.totalOutstandingAmount || 0)}
- Open Invoices: ${summary.totalOutstandingInvoices || 0}

Why it matters:
- This is tied-up cash that has not converted yet
- Higher AR requires stronger collection prioritization and follow-up cadence

Click to open invoice-level ledger for collection sequencing.`,
              color: "warning.main",
              onClick: () => openCollectionsDrawer(data, "all"),
            },
            {
              title: "Overdue AR",
              value: formatCurrency(summary.overdueAmount || 0),
              subtitle: `${summary.overdueInvoices || 0} overdue invoices`,
              tooltipTitle: "Overdue AR",
              tooltipContent: `Portion of receivables past invoice due date.

Current period context:
- Overdue Amount: ${formatCurrency(summary.overdueAmount || 0)}
- Overdue Invoices: ${summary.overdueInvoices || 0}

Interpretation:
- Rising overdue AR is an early warning for cash-flow pressure
- Concentrated overdue balances often require account-level escalation

Use this with aging buckets to target highest-impact follow-up first.`,
              color: "error.main",
              onClick: () => openCollectionsDrawer(data, "overdue"),
            },
            {
              title: "Critical 90+ Days",
              value: formatCurrency(summary.criticalAmount || 0),
              subtitle: `${summary.criticalInvoices || 0} invoices`,
              tooltipTitle: "Critical 90+ Days",
              tooltipContent: `Receivables aged more than 90 days past due, representing the highest recovery-risk segment.

Current period context:
- 90+ Amount: ${formatCurrency(summary.criticalAmount || 0)}
- 90+ Invoices: ${summary.criticalInvoices || 0}

Interpretation:
- Older debt has lower probability of full recovery
- This should be a priority queue for escalation and recovery workflows

Click for invoice-level critical list.`,
              color: "error.main",
              onClick: () => openCollectionsDrawer(data, "critical"),
            },
            {
              title: "Collection Rate",
              value: `${(summary.collectionRate || 0).toFixed(2)}%`,
              subtitle: `${(summary.avgDaysPastDue || 0).toFixed(1)} avg overdue days`,
              tooltipTitle: "Collection Rate",
              tooltipContent: `Tracks how effectively billed value is converted into collected cash within the reporting scope.

Current period context:
- Collection Rate: ${(summary.collectionRate || 0).toFixed(2)}%
- Average Days Past Due: ${(summary.avgDaysPastDue || 0).toFixed(1)} days

How to interpret:
- Higher collection rate with lower past-due days indicates healthy cash conversion
- Low collection rate combined with rising delay signals AR process breakdown

Use this KPI with overdue and critical aging cards for full collections health.`,
              onClick: () => openCollectionsDrawer(data, "all"),
            },
          ],
        },
      ];
    },
    [formatCurrency, openCollectionsDrawer, openPaymentMethodDrawer],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Revenue by Payment Method"
            tooltipTitle="Revenue by Payment Method"
            tooltipContent="Breaks down total revenue share by payment rail, helping identify processing concentration risk and where fee optimization can improve net margin. Click a slice to drill into method-level performance and transaction economics."
            data={(data.methodsChart || []).map((row) => ({
              id: row.id,
              label: row.label,
              value: row.value,
            }))}
            height={360}
            showLegend={true}
            legendPosition="right"
            valueFormatter={formatCurrency}
            onSliceClick={(slice) => openPaymentMethodDrawer(data, slice.label)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Payment Timing Ranges (Client Count)"
            tooltipTitle="Payment Timing Ranges (Client Count)"
            tooltipContent="Shows how many clients fall into each payment-speed bucket (early/on-time/late windows). Use this to understand behavior distribution and identify segments that need tighter payment terms or proactive reminders."
            data={(data.rangesRows || []).map((row) => ({
              label: row.label,
              clientCount: row.clientCount || 0,
              totalRevenue: row.totalRevenue || 0,
            }))}
            xAxisKey="label"
            series={[
              {
                dataKey: "clientCount",
                label: "Clients",
                color: chartColors.info,
              },
            ]}
            height={360}
            showLegend={false}
            yAxisLabel="Clients"
            yAxisScaleType="linear"
            valueFormatter={(value) => `${value || 0} clients`}
            onBarClick={(bar) => openPaymentRangeDrawer(data, bar.label)}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ReportBarChart
            title="Payment Timing Exposure by Range"
            tooltipTitle="Payment Timing Exposure by Range"
            tooltipContent="Displays dollar exposure associated with each payment-speed range. This helps prioritize collection focus on ranges where both delay and financial exposure are highest, not just client count."
            data={(data.rangesRows || []).map((row) => ({
              label: row.label,
              exposure: row.totalRevenue || 0,
              avgPaymentDays: row.avgPaymentDays || 0,
            }))}
            xAxisKey="label"
            series={[
              {
                dataKey: "exposure",
                label: "Invoice Exposure",
                color: chartColors.warning,
              },
            ]}
            height={340}
            showLegend={false}
            yAxisLabel="Amount ($)"
            yAxisScaleType="linear"
            valueFormatter={formatCurrency}
            onBarClick={(bar) => openPaymentRangeDrawer(data, bar.label)}
          />
        </Grid>
      </Grid>
    ),
    [chartColors.info, chartColors.warning, formatCurrency, openPaymentMethodDrawer, openPaymentRangeDrawer],
  );

  return (
    <>
      <DashboardReport
        title="Payment & Collections"
        description="Unified view of payment method mix, client payment timing behavior, and AR collections pressure."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

// Export map for easy lookup
export const FINANCIAL_REPORTS = {
  "revenue-summary": RevenueSummary,
  "revenue-by-client": RevenueByClient,
  "revenue-by-service": RevenueByService,
  "revenue-by-language": RevenueByLanguage,
  "profit-margin": ProfitMargin,
  "service-profitability": ServiceProfitability,
  "payment-method": PaymentMethod,
};
